// src/admin/ApplicationCalendarPage.jsx
import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Search,
  Filter,
  Eye,
  X,
  Download,
  ChevronDown,
  Calendar as CalendarIcon,
  User,
  FileText,
  MessageSquare,
  Clock,
  Mail,
  Phone,
  CheckCircle,
  RefreshCw,
  Send,
  Edit,
  ArrowUpDown,
  Home,
  Plus,
  UserCheck,
  Upload,
  Building,
  FileCheck,
  Tag,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Bell,
  AlertCircle,
  PlayCircle,
  Info,
  Trash2,
  Loader2,
  Package,
} from "lucide-react";
import { listApplications, updateApplication, getApplication, deleteApplication } from "../../api/borrowerPortal/applications";
import { getInactiveLenders } from "../../api/admin/inactiveLenders";
import { getMortgageProducts } from "../../api/lenderPortal/listedproducts";
import {
  extractIllustrationData,
  generateClientIllustrationPDFWithLogo as generateClientIllustrationPDF,
  generateEvidenceOfResearchPDFWithLogo as generateEvidenceOfResearchPDF,
} from "../../utils/generateClientIllustration.js";
import { getShortlistedProducts } from "../../api/borrowerPortal/shortlistedModal";
import { api } from "../../api/client";
import { downloadFrappeFile } from "../../utils/frappeFileDownload";
import {
  downloadAllApplicationDocuments,
  buildStaticDownloadDocuments,
  mapBorrowerDocumentsToDownload,
} from "../../utils/downloadApplicationDocuments.js";
import { TableSkeleton } from "../../components/ui/Skeleton.jsx";
import {
  getInvoiceFinanceSourcingFilters,
  buildInvoiceFinanceSourcingPayload,
} from "../../api/sourcing-api/invoiceFinanceSourcingApi.js";
import {
  mapSourcingApiProducts,
  filterInvoiceFinanceSourcingProducts,
  matchesInvoiceFinanceSector,
  isExcludedInvoiceFinanceSector,
  productMatchesFacilityBand,
  productMatchesInvoiceEligibility,
  resolveInvoiceFinanceFacilityType,
} from "../../api/invoiceFinanceSourcing.js";

/**
 * Daily Application Diary - Time to Cash Tracking
 * 
 * Row 1: Ongoing Applications (Time to Cash tracking by loan purpose)
 * Row 2: Lender Portal Activity Alerts (30+ days inactive)
 * Row 3: Scheduled Products (Going live today)
 * 
 * Time to Cash by Loan Purpose:
 * - Purchase: Pre-enquiry → Enquiry(3d) → Application(4d) → Approval(15d) → Valuation(25d) → Legal(4d) → Completion
 * - Remortgage: Pre-enquiry → Enquiry(3d) → Application(4d) → Approval(21d) → Valuation(14d) → Legal(4d) → Completion
 * - Product Transfer: Pre-enquiry → Enquiry(3d) → Application(4d) → Approval(3d) → Valuation(6d) → Legal(4d) → Completion
 */

/* =============================================================================
   Demo file downloads
============================================================================= */
const DEMO_DOCS = {
  eor: {
    key: "eor",
    label: "EoR",
    full: "Evidence of Research & Illustration",
    filename: "EoR 5yr Fixed 07072025.pdf",
  },
  esis: {
    key: "esis",
    label: "ESIS",
    full: "Illustration (ESIS)",
    filename: "ESIS Example v0.1 20251013 C&I.docx",
  },
};

const docHref = (filename) => `/docs/${encodeURIComponent(filename)}`;

// Money formatter
const money = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string" && v.startsWith("£")) return v;
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(Number(v));
  } catch {
    return String(v);
  }
};

// Time to Cash Configuration by Loan Purpose (in days)
const TIME_TO_CASH_CONFIG = {
  Purchase: {
    "Pre-enquiry": 0,
    Enquiry: 3,
    Application: 4,
    Approval: 15,
    Valuation: 25,
    Legal: 4,
    Completion: 0,
  },
  Remortgage: {
    "Pre-enquiry": 0,
    Enquiry: 3,
    Application: 4,
    Approval: 21,
    Valuation: 14,
    Legal: 4,
    Completion: 0,
  },
  "Product Transfer": {
    "Pre-enquiry": 0,
    Enquiry: 3,
    Application: 4,
    Approval: 3,
    Valuation: 6,
    Legal: 4,
    Completion: 0,
  },
  "Buy-to-Let": {
    "Pre-enquiry": 0,
    Enquiry: 3,
    Application: 4,
    Approval: 15,
    Valuation: 25,
    Legal: 4,
    Completion: 0,
  },
  Development: {
    "Pre-enquiry": 0,
    Enquiry: 3,
    Application: 5,
    Approval: 21,
    Valuation: 30,
    Legal: 7,
    Completion: 0,
  },
  Bridging: {
    "Pre-enquiry": 0,
    Enquiry: 2,
    Application: 3,
    Approval: 7,
    Valuation: 10,
    Legal: 3,
    Completion: 0,
  },
  Commercial: {
    "Pre-enquiry": 0,
    Enquiry: 3,
    Application: 5,
    Approval: 21,
    Valuation: 30,
    Legal: 7,
    Completion: 0,
  },
};

// Application Stages for Time to Cash
const APPLICATION_STAGES = [
  { value: "Pre Enquiry", label: "Pre-enquiry", color: "slate" },
  { value: "Enquiry", label: "Enquiry", color: "purple" },
  { value: "Application", label: "Application", color: "blue" },
  { value: "Approval", label: "Approval", color: "blue" },
  { value: "Valuation", label: "Valuation", color: "blue" },
  { value: "Audit", label: "Audit", color: "cyan" },
  // Backend expects "Legals" as the status value; keep label as "Legal" for UI
  { value: "Legals", label: "Legal", color: "yellow" },
  { value: "Completion", label: "Completion", color: "green" },
  { value: "Not Progressing", label: "Not Progressing", color: "red" },
];

const PRODUCT_LINE_OPTIONS = ["BTL", "IF"];

const STATUS_TO_STAGE_DATE_FIELD = {
  "Pre Enquiry": "pre_enquiry_date",
  Enquiry: "enquiry_date",
  Application: "application_date",
  Approval: "approval_date",
  Valuation: "valuation_date",
  Audit: "valuation_date",
  Legals: "legal_date",
  Completion: "completion_date",
};

// Team members for assignment
const TEAM_MEMBERS = [
  { id: "matt-mander", name: "Matt Mander", email: "matt@dynamiccf.co.uk" },
  { id: "josh-mander", name: "Josh Mander", email: "josh@dynamiccf.co.uk" },
  { id: "matt-usher", name: "Matt Usher", email: "matt@ushercommercial.co.uk" },
  { id: "chris-ball", name: "Chris Ball", email: "chris@ideatefinance.co.uk" },
  { id: "troy-edwards", name: "Troy Edwards", email: "troy@midlandcommercialfinanceltd.co.uk" },
  { id: "john-horrocks", name: "John Horrocks", email: "john@merseysidecommercial.co.uk" },
  { id: "amit-patel", name: "Amit Patel", email: "amit@dynamiccf.co.uk" },
  { id: "andrew-coombes", name: "Andrew Coombes", email: "andrew@dynamiccf.co.uk" },
];

// Stage info text for activity timeline (same as TrackApplications)
const STAGE_INFO_TEXT = {
  preenquiry: "Initial contact and fact-finding before a formal enquiry is submitted.",
  enquiry: "Formal enquiry has been submitted to the lender.",
  application: "Full application has been submitted and is being processed.",
  approval: "Lender has approved the application in principle.",
  valuation: "Property valuation is being arranged or has been completed.",
  audit: "Facility audit is being arranged or has been completed.",
  legal: "Legal checks and lender conditions are being completed prior to release of funds.",
  completion: "Funds have been released and the mortgage application is completed.",
};

function isInvoiceFinanceApplication(source = {}) {
  const raw = source._raw || source;
  const cat = String(
    source.productCategory ||
      source.product_category ||
      source.category ||
      source.productType ||
      raw.product_category ||
      raw.category ||
      raw.product_type ||
      ""
  )
    .trim()
    .toUpperCase();
  const purpose = String(source.loan_purpose || raw.loan_purpose || "").toLowerCase();
  const id = String(source.id || source.name || raw.name || "").toUpperCase();
  return (
    cat === "IF" ||
    cat.includes("INVOICE") ||
    purpose.includes("invoice") ||
    id.startsWith("IF-")
  );
}

function formatContractTermLabel(source = {}) {
  const raw = source._raw || source;
  const months = Number(
    source.contract_term_months ??
      source.termMonths ??
      source.term_months ??
      raw.contract_term_months ??
      raw.term_months ??
      raw.termMonths
  );
  if (Number.isFinite(months) && months > 0) {
    return `${Math.round(months)} month${Math.round(months) === 1 ? "" : "s"}`;
  }
  return "—";
}

function formatIfApplicationTitle(source = {}) {
  const label = resolveIfFacilityType(source) || "Disclosed";
  return `${label} - ${formatContractTermLabel(source)}`;
}

/** Confidential | Disclosed for IF apps; null if not resolvable */
function resolveIfFacilityType(source = {}) {
  const raw = source._raw || source;
  const facilityType = String(
    source.facility_type ??
      source.facilityType ??
      source.facilityTypeDisplay ??
      raw.facility_type ??
      ""
  )
    .trim()
    .toLowerCase();
  if (facilityType === "confidential") return "Confidential";
  if (facilityType === "disclosure" || facilityType === "disclosed") return "Disclosed";

  const conf =
    source.confidential_facility ??
    source.confidential ??
    raw.confidential_facility ??
    raw.confidential;
  if (conf === true || conf === 1 || conf === "1") return "Confidential";

  const disclosure = source.disclosure ?? raw.disclosure;
  if (disclosure === true || disclosure === 1 || disclosure === "1") return "Disclosed";

  // Default IF apps without explicit disclosure flag to Disclosed when not confidential
  if (conf === false || conf === 0 || conf === "0") return "Disclosed";
  return null;
}

const IF_FACILITY_OPTIONS = ["Confidential", "Disclosed"];

const SORT_OPTION_LABELS = {
  modified: "Updated",
  amount: "Amount",
  borrower: "Borrower",
  productLine: "Product",
  applicationType: "Application Type",
  loanPurpose: "Loan Purpose",
  facility: "Facility",
};

const getApplicationTypeSortKey = (app) => {
  if ((app?.productLine || "BTL") === "IF") return String(app?.facilityType || "");
  return String(app?.loanPurpose || app?.productType || "");
};

// Activity timeline stages (same logic as TrackApplications)
const getActivities = (app, rawData = {}) => {
  const activities = [];
  const status = (app.status || "").trim();
  const statusLower = status.toLowerCase();
  const isIf = isInvoiceFinanceApplication({ ...app, ...rawData, _raw: rawData });
  const stages = [
    { key: "preenquiry", title: "Pre-enquiry", status: "Pre Enquiry", dateField: "pre_enquiry_date" },
    { key: "enquiry", title: "Enquiry", status: "Enquiry", dateField: "enquiry_date" },
    { key: "application", title: "Application", status: "Application", dateField: "application_date" },
    { key: "approval", title: "Approval", status: "Approval", dateField: "approval_date" },
    {
      key: "valuation",
      title: isIf ? "Audit" : "Valuation",
      status: isIf ? "Audit" : "Valuation",
      dateField: "valuation_date",
    },
    { key: "legal", title: "Legal", status: "Legals", dateField: "legal_date" },
    { key: "completion", title: "Completion", status: "Completion", dateField: "completion_date" },
  ];
  let currentStageIndex = -1;
  stages.forEach((stage, idx) => {
    let isMatch = false;
    if (stage.key === "preenquiry") {
      isMatch = status === "Pre Enquiry" || statusLower.includes("pre enquiry") || statusLower.includes("pre-enquiry");
    } else if (stage.key === "enquiry") {
      isMatch = (status === "Enquiry" || statusLower.includes("enquiry")) && !statusLower.includes("pre");
    } else if (stage.key === "legal") {
      isMatch = statusLower.includes("legal");
    } else if (stage.key === "valuation") {
      isMatch =
        status === stage.status ||
        statusLower.includes("valuation") ||
        statusLower.includes("audit") ||
        status === "Valuation" ||
        status === "Audit";
    } else {
      isMatch = status === stage.status || statusLower.includes(stage.key);
    }
    if (isMatch) currentStageIndex = idx;
  });
  const submittedOn = app.submitted_on || app.submittedDate || rawData.creation || rawData.date_time || "";
  const modifiedOn = app.modified || app.modified_on || rawData.modified || "";
  let submissionDateTime = "";
  let modifiedDateTime = "";
  const normalizeDate = (dateStr) => {
    if (!dateStr) return "";
    let clean = String(dateStr).trim().replace(/\.\d+$/, "");
    if (clean.includes(" ") && !clean.includes("T")) clean = clean.replace(" ", "T");
    if (!clean.includes(":")) clean = `${clean}T00:00:00`;
    return clean;
  };
  if (submittedOn) submissionDateTime = normalizeDate(typeof submittedOn === "object" && submittedOn instanceof Date ? submittedOn.toISOString() : submittedOn);
  if (modifiedOn) modifiedDateTime = normalizeDate(typeof modifiedOn === "object" && modifiedOn instanceof Date ? modifiedOn.toISOString() : modifiedOn);
  const statusChangedOn = normalizeDate(rawData.status_changed_on || rawData.status_changed_at || "");
  const stageStartDate = normalizeDate(rawData.stage_start_date || rawData.stage_start_on || "");

  stages.forEach((stage, idx) => {
    let activityStatus = "pending";
    let date = "";
    const backendDate =
      rawData[stage.dateField] ||
      rawData[`${stage.key}_date`] ||
      rawData[`${stage.key}Date`] ||
      rawData[`${stage.key}_changed_on`] ||
      rawData[`${stage.status.toLowerCase().replace(/\s+/g, "_")}_date`] ||
      rawData[`${stage.status.toLowerCase().replace(/\s+/g, "_")}_changed_on`] ||
      rawData[`${stage.key}_on`] ||
      (stage.key === "valuation" ? rawData.audit_date || rawData.audit_changed_on : "") ||
      "";
    if (currentStageIndex > -1) {
      if (idx < currentStageIndex) {
        activityStatus = "completed";
        if (backendDate) date = normalizeDate(backendDate);
        else if (idx === 0 && submissionDateTime) date = submissionDateTime;
      } else if (idx === currentStageIndex) {
        activityStatus = "inprogress";
        if (backendDate) date = normalizeDate(backendDate);
        else if (statusChangedOn) date = statusChangedOn;
        else if (stageStartDate) date = stageStartDate;
        else if (modifiedDateTime) date = modifiedDateTime;
        else if (submissionDateTime) date = submissionDateTime;
      }
    } else if (submissionDateTime && idx === 0) {
      activityStatus = "inprogress";
      date = submissionDateTime;
    }
    activities.push({ key: stage.key, title: stage.title, date, status: activityStatus });
  });

  if (currentStageIndex > 0) {
    for (let i = currentStageIndex - 1; i >= 0; i -= 1) {
      if (activities[i].date) continue;
      for (let j = i + 1; j <= currentStageIndex; j += 1) {
        if (activities[j].date) {
          activities[i].date = activities[j].date;
          break;
        }
      }
      if (!activities[i].date && statusChangedOn) {
        activities[i].date = statusChangedOn;
      }
    }
  }

  return activities;
};

// Removed mock data generators - now using real APIs

// Status badge component
function StatusBadge({ status, tone }) {
  const toneClasses = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    purple: "bg-violet-100 text-violet-800 border-violet-200",
    blue: "bg-sky-100 text-sky-800 border-sky-200",
    cyan: "bg-cyan-100 text-cyan-800 border-cyan-200",
    yellow: "bg-amber-100 text-amber-800 border-amber-200",
    green: "bg-emerald-100 text-emerald-800 border-emerald-200",
    red: "bg-rose-100 text-rose-800 border-rose-200",
  };
  const cls = toneClasses[tone] || toneClasses.blue;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${cls}`}>
      {status}
    </span>
  );
}

function FilterDropdown({ label, options, value, onChange, icon: Icon, optionLabels }) {
  const [open, setOpen] = useState(false);
  const formatOption = (option) =>
    optionLabels?.[option] ||
    String(option).replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^\w/, (c) => c.toUpperCase());

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition ${value ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
          }`}
      >
        {Icon && <Icon className="h-3 w-3" />}
        {label}
        {value && (
          <span className="ml-1 px-1.5 py-0.5 bg-sky-100 text-sky-700 rounded text-[11px]">
            {formatOption(value)}
          </span>
        )}
        <ChevronDown className={`h-3 w-3 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-10 mt-2 w-56 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          <button
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-50 transition ${!value ? "bg-slate-50 text-slate-900 font-medium" : "text-slate-700"
              }`}
          >
            All
          </button>

          {options
            .filter((o) => o && o !== "Unassigned")
            .map((option) => (
              <button
                key={option}
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-50 transition ${value === option ? "bg-sky-50 text-sky-700 font-medium" : "text-slate-700"
                  }`}
              >
                {formatOption(option)}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

// Helper functions
const formatRelativeTime = (date) => {
  if (!date) return "—";
  const now = new Date();
  const diff = now - new Date(date);
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(date).toLocaleDateString("en-GB");
};

const formatDate = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const calculateDaysSince = (dateStr) => {
  if (!dateStr) return 0;
  const date = new Date(dateStr);
  const today = new Date();
  const diff = today - date;
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

// Time to Cash Progress Component (displayed in table)
function TimeToCashProgress({ application }) {
  const productType = application.productType || "Purchase";
  const config = TIME_TO_CASH_CONFIG[productType] || TIME_TO_CASH_CONFIG.Purchase;
  const currentStage = application.currentStage;

  const daysInStage = calculateDaysSince(application.stageStartDate);
  const idealDaysForStage = config[currentStage] || 0;
  const totalDaysElapsed = calculateDaysSince(application.applicationDate);

  const stages = Object.keys(config);
  const currentStageIndex = stages.indexOf(currentStage);
  let idealTotalDays = 0;
  for (let i = 0; i <= currentStageIndex; i++) {
    idealTotalDays += config[stages[i]];
  }

  const remainingIdealDays = idealTotalDays - totalDaysElapsed;
  const isDelayed = daysInStage > idealDaysForStage;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-slate-600">
          Day <span className="font-semibold">{daysInStage}</span>/{idealDaysForStage}
        </div>
        <div className={`text-[11px] font-medium ${isDelayed ? "text-red-600" : "text-green-600"}`}>
          {isDelayed ? `${daysInStage - idealDaysForStage}d late` : "On track"}
        </div>
      </div>

      <div className="relative h-1 bg-slate-200 rounded-full overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 rounded-full transition-all ${isDelayed ? "bg-red-500" : "bg-green-500"
            }`}
          style={{ width: `${Math.min((daysInStage / idealDaysForStage) * 100, 100)}%` }}
        />
      </div>

      <div className="flex items-center justify-between text-[10px] text-slate-500">
        <span>Total: {totalDaysElapsed}d</span>
        <span>Est: {Math.max(remainingIdealDays, 0)}d left</span>
      </div>
    </div>
  );
}

// Helper function for full date time
const formatFullDateTime = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Application detail modal
function ApplicationDetailModal({ application, onClose, onOpenStatusUpdate, onOpenAssignModal }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [activeInfoPopup, setActiveInfoPopup] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState("");
  const [downloadingAll, setDownloadingAll] = useState(false);

  const tabs = [
    { id: "overview", label: "Overview", icon: Eye },
    { id: "documents", label: "Documents", icon: FileText },
    { id: "timeline", label: "Timeline", icon: Clock },
  ];

  const getStatusIcon = (status) => {
    const iconMap = {
      Enquiry: Send,
      Application: Eye,
      Approval: FileCheck,
      Valuation: Home,
      Audit: Home,
      Legals: FileText,
      Completion: CheckCircle,
    };
    return iconMap[status] || Clock;
  };

  const appForActivity = {
    status: application.status,
    submitted_on: application._raw?.submitted_on ?? (application.submittedDate && (application.submittedDate instanceof Date ? application.submittedDate.toISOString() : application.submittedDate)),
    submittedDate: application.submittedDate,
    modified: application._raw?.modified ?? (application.modified && (application.modified instanceof Date ? application.modified.toISOString() : application.modified)),
  };
  const activity = getActivities(appForActivity, application._raw || {});

  useEffect(() => {
    let cancelled = false;
    async function loadDocuments() {
      setDocumentsLoading(true);
      setDocumentsError("");
      try {
        const filters = JSON.stringify([
          ["attached_to_doctype", "=", "Applications"],
          ["attached_to_name", "=", application.id],
        ]);
        const response = await api.get("/api/resource/File", {
          params: {
            fields: JSON.stringify(["name", "file_name", "file_url", "attached_to_field", "is_private"]),
            filters,
            limit_page_length: 100,
          },
        });
        if (cancelled) return;
        const files = response?.data?.data || response?.data?.message?.data || [];
        setDocuments(files);
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading application documents:", err);
        setDocumentsError("Failed to load documents. Please try again.");
      } finally {
        if (!cancelled) setDocumentsLoading(false);
      }
    }
    loadDocuments();
    return () => {
      cancelled = true;
    };
  }, [application.id]);

  const handleDownloadAllDocuments = async () => {
    setDownloadingAll(true);
    try {
      const docs = [
        ...buildStaticDownloadDocuments(),
        ...mapBorrowerDocumentsToDownload(documents),
      ];
      await downloadAllApplicationDocuments({
        applicationId: application.id,
        documents: docs,
      });
    } catch (error) {
      console.error("Download all failed:", error);
      window.alert("Could not download all documents. Please try again.");
    } finally {
      setDownloadingAll(false);
    }
  };

  const formatActivityDateTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const day = date.getDate();
      const month = date.toLocaleDateString("en-GB", { month: "short" });
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day} ${month} ${year} - ${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{application.id}</h2>
              <p className="text-sm text-slate-500">Submitted {formatRelativeTime(application.submittedDate)}</p>
            </div>
            <StatusBadge
              status={
                isInvoiceFinanceApplication(application) &&
                (application.status === "Valuation" ||
                  String(application.status || "").toLowerCase().includes("valuation") ||
                  application.currentStage === "Valuation")
                  ? "Audit"
                  : application.status
              }
              tone={application.statusTone}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onOpenStatusUpdate(application)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-sky-600 bg-sky-50 rounded-lg hover:bg-sky-100 transition"
            >
              <Edit className="h-4 w-4" />
              Update Status
            </button>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition">
              <X className="h-5 w-5 text-slate-500" />
            </button>
          </div>
        </div>

        <div className="px-6 border-b border-slate-200 flex gap-1 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${activeTab === tab.id
                    ? "border-sky-500 text-sky-600"
                    : "border-transparent text-slate-600 hover:text-slate-900"
                  }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* Borrower */}
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Borrower information
                </h3>
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <User className="h-4 w-4 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-500">Name</div>
                      <div className="text-sm font-medium text-slate-900">{application.borrower.name}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="h-4 w-4 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-500">Email</div>
                      <div className="text-sm text-slate-700">{application.borrower.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="h-4 w-4 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-500">Phone</div>
                      <div className="text-sm text-slate-700">{application.borrower.phone}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Product details from application */}
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Product details
                </h3>
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <Building className="h-4 w-4 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-500">Product</div>
                      <div className="text-sm font-semibold text-slate-900">
                        {application.product?.productId || "—"} • {application.product?.lender || "—"}
                      </div>
                      {!isInvoiceFinanceApplication(application) && application.product?.name ? (
                        <div className="text-sm text-slate-700">
                          {application.product.name}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
                    <div className="bg-white border border-slate-200 rounded-xl p-3">
                      <div className="text-xs text-slate-500">Loan amount</div>
                      <div className="text-sm font-semibold text-slate-900">
                        {money(application.loanAmount)}
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-3">
                      <div className="text-xs text-slate-500">LTV</div>
                      <div className="text-sm font-semibold text-slate-900">
                        {application.ltv || application.ltv === 0 ? `${application.ltv}%` : "—"}
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-3">
                      <div className="text-xs text-slate-500">Rate</div>
                      <div className="text-sm font-semibold text-slate-900">
                        {application.product?.rate != null ? `${application.product.rate}%` : "—"}
                      </div>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-3">
                      <div className="text-xs text-slate-500">
                        {isInvoiceFinanceApplication(application) ? "Contract term" : "Product term"}
                      </div>
                      <div className="text-sm font-semibold text-slate-900">
                        {application.product?.termMonths != null
                          ? `${application.product.termMonths} months`
                          : "—"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Assignment */}
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <UserCheck className="h-4 w-4" />
                  Assignment
                </h3>
                <div className="bg-slate-50 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-500">Assigned To</div>
                    <div className="text-sm font-medium text-slate-900">{application.assignedTo}</div>
                    {application.caseEmail && <div className="text-xs text-slate-500 mt-1">{application.caseEmail}</div>}
                  </div>
                  <button
                    onClick={() => onOpenAssignModal(application)}
                    className="px-4 py-2 text-sm font-medium text-white bg-purple-500 rounded-lg hover:bg-purple-600 transition"
                  >
                    Reassign
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === "documents" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-slate-900">
                  Documents{documents.length > 0 ? ` (${documents.length})` : ""}
                </h3>
                <button
                  type="button"
                  onClick={handleDownloadAllDocuments}
                  disabled={downloadingAll || documentsLoading}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-sky-500 rounded-lg hover:bg-sky-600 transition disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
                >
                  {downloadingAll ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                  {downloadingAll ? "Downloading..." : "Download All"}
                </button>
              </div>

              {documentsLoading ? (
                <div className="py-8 text-center text-slate-500 text-sm border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
                  Loading documents…
                </div>
              ) : documentsError ? (
                <div className="py-4 px-4 border border-amber-200 rounded-xl bg-amber-50 text-xs text-amber-800">
                  {documentsError}
                </div>
              ) : documents.length === 0 ? (
                <div className="py-8 text-center text-slate-500 text-sm border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
                  No documents attached to this application.
                </div>
              ) : (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div
                      key={doc.name}
                      className="flex items-center justify-between px-3 py-2 border border-slate-200 rounded-lg bg-white"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-slate-400" />
                        <div>
                          <div className="text-sm font-medium text-slate-900">
                            {doc.file_name || doc.name}
                          </div>
                          {doc.attached_to_field && (
                            <div className="text-[11px] text-slate-500">
                              Field: {doc.attached_to_field}
                            </div>
                          )}
                        </div>
                      </div>
                      {doc.file_url && (() => {
                        const rawFilename = doc.file_name || doc.file_url.split("/").pop() || "download";

                        const handleDownload = async () => {
                          try {
                            await downloadFrappeFile({
                              fileUrl: doc.file_url,
                              fileId: doc.name,
                              downloadName: rawFilename,
                              isPrivate: doc.is_private || doc.file_url.startsWith("/private/"),
                            });
                          } catch (err) {
                            console.error("Download failed:", err);
                            alert("Failed to download file. Please try again.");
                          }
                        };

                        return (
                          <button
                            onClick={handleDownload}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition"
                          >
                            <Download className="h-3.5 w-3.5" />
                            Download
                          </button>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "timeline" && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-900">Activity timeline</h3>
              <div className="relative max-h-[min(60vh,400px)] overflow-y-auto overflow-x-auto py-2 px-1">
                <div className="relative min-w-[600px] py-5 px-2">
                  {/* Gradient line */}
                  <div
                    className="absolute left-6 right-6 top-8 h-0.5 rounded-full z-0"
                    style={{
                      background: (() => {
                        const total = activity.length;
                        const stops = activity.map((step, idx) => {
                          const stageColors = { preenquiry: "#64748b", enquiry: "#3b82f6", application: "#0ea5e9", approval: "#a855f7", valuation: "#f59e0b", legal: "#ec4899", completion: "#10b981" };
                          const pct = total > 1 ? (idx / (total - 1)) * 100 : 0;
                          const color = step.status === "pending" ? "#e5e7eb" : (stageColors[step.key] || "#3b82f6");
                          return `${color} ${pct}%`;
                        });
                        return `linear-gradient(to right, ${stops.join(", ")})`;
                      })(),
                    }}
                  />
                  {/* Steps */}
                  <div className="relative grid grid-cols-7 gap-0 min-w-[600px] z-10">
                    {activity.map((step, idx) => {
                      const stageColors = { preenquiry: { completed: "#64748b", inprogress: "#64748b", pending: "#e5e7eb" }, enquiry: { completed: "#3b82f6", inprogress: "#3b82f6", pending: "#e5e7eb" }, application: { completed: "#0ea5e9", inprogress: "#0ea5e9", pending: "#e5e7eb" }, approval: { completed: "#a855f7", inprogress: "#a855f7", pending: "#e5e7eb" }, valuation: { completed: "#f59e0b", inprogress: "#f59e0b", pending: "#e5e7eb" }, legal: { completed: "#ec4899", inprogress: "#ec4899", pending: "#e5e7eb" }, completion: { completed: "#10b981", inprogress: "#10b981", pending: "#e5e7eb" } };
                      const sc = stageColors[step.key] || stageColors.preenquiry;
                      let circleBg = sc.pending, circleBorder = sc.pending, textColor = "#64748b", badgeBg = "#f1f5f9", badgeText = "#64748b", badgeBorder = "#e5e7eb";
                      if (step.status === "completed") { circleBg = sc.completed; circleBorder = sc.completed; textColor = sc.completed; }
                      else if (step.status === "inprogress") { circleBg = sc.inprogress; circleBorder = sc.inprogress; textColor = sc.inprogress; badgeBg = sc.inprogress; badgeText = "#fff"; badgeBorder = sc.inprogress; }
                      return (
                        <div key={`${step.key}-${idx}`} className="flex flex-col items-center text-center">
                          <div className="relative inline-block">
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center border-2 flex-shrink-0"
                              style={{ backgroundColor: circleBg, borderColor: circleBorder, boxShadow: step.status !== "pending" ? "0 2px 8px rgba(0,0,0,0.1)" : "none" }}
                            >
                              {step.status === "completed" ? <CheckCircle className="h-5 w-5 text-white" /> : step.status === "inprogress" ? <Clock className="h-5 w-5 text-white" /> : <div className="w-3 h-3 rounded-full bg-slate-300" />}
                            </div>
                            {STAGE_INFO_TEXT[step.key] && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setActiveInfoPopup(activeInfoPopup === step.key ? null : step.key); }}
                                className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center border-2 border-sky-500 bg-sky-100 text-sky-600 cursor-pointer z-10"
                                title="Info"
                              >
                                <Info className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                          <div className="mt-2 flex flex-col items-center min-w-0">
                            <span className="text-xs font-semibold truncate max-w-full" style={{ color: textColor }}>{step.title}</span>
                            {(step.status === "completed" || step.status === "inprogress") && step.date && <span className="text-[10px] text-slate-400 mt-0.5">{formatActivityDateTime(step.date)}</span>}
                            <span className="text-[10px] font-semibold px-2 py-1 rounded-full mt-1 inline-flex" style={{ backgroundColor: badgeBg, color: badgeText, border: `1px solid ${badgeBorder}` }}>{step.status === "completed" ? "Completed" : step.status === "inprogress" ? "In Progress" : "Pending"}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              {activeInfoPopup && STAGE_INFO_TEXT[activeInfoPopup] && (
                <div className="mt-2 p-3 bg-sky-50 border border-sky-200 rounded-lg text-xs text-slate-700">
                  <span className="font-semibold text-slate-900">{activity.find(s => s.key === activeInfoPopup)?.title}: </span>
                  {STAGE_INFO_TEXT[
                    activeInfoPopup === "valuation" && isInvoiceFinanceApplication(application)
                      ? "audit"
                      : activeInfoPopup
                  ]}
                  <button type="button" onClick={() => setActiveInfoPopup(null)} className="ml-2 text-sky-600 hover:underline">Close</button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="text-xs text-slate-500">Last updated: {formatRelativeTime(application.lastUpdated)}</div>
        </div>
      </div>
    </div>
  );
}

// Status update modal
function StatusUpdateModal({ application, onClose, onUpdateStatus }) {
  const isIf = isInvoiceFinanceApplication(application);
  const stageOptions = APPLICATION_STAGES.filter((stage) =>
    isIf ? stage.value !== "Valuation" : stage.value !== "Audit"
  );
  const initialStatus = isIf
    ? application.status === "Valuation" ? "Audit" : application.status
    : application.status === "Audit" ? "Valuation" : application.status;
  const [status, setStatus] = useState(initialStatus);
  const [note, setNote] = useState("");

  const handleSubmit = () => {
    onUpdateStatus(application.id, status, note);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Update Status</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">New Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400"
            >
              {stageOptions.map((stage) => (
                <option key={stage.value} value={stage.value}>
                  {stage.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Note (Optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note about this status change..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400 resize-none"
              rows={3}
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-sky-500 rounded-lg hover:bg-sky-600 transition"
            >
              Update Status
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Assign modal
function AssignModal({ application, onClose, onAssign }) {
  const [selectedMember, setSelectedMember] = useState("");

  const handleAssign = () => {
    const member = TEAM_MEMBERS.find((m) => m.id === selectedMember);
    if (member) {
      onAssign(application.id, member);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Assign Application</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="p-4 bg-slate-50 rounded-xl">
            <div className="text-sm font-medium text-slate-900">{application.id}</div>
            <div className="text-sm text-slate-600">{application.borrower.name}</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Select Team Member</label>
            <select
              value={selectedMember}
              onChange={(e) => setSelectedMember(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400"
            >
              <option value="">Choose a team member...</option>
              {TEAM_MEMBERS.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-end gap-2 pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={!selectedMember}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-500 rounded-lg hover:bg-purple-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Assign
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Component
export default function ApplicationCalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [applications, setApplications] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [scheduledProducts, setScheduledProducts] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [statusUpdateApp, setStatusUpdateApp] = useState(null);
  const [assignModalApp, setAssignModalApp] = useState(null);
  const [deleteApp, setDeleteApp] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Selected date for scheduled products (default to today)
  const [selectedScheduledDate, setSelectedScheduledDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });

  // Loading state
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [productLineFilter, setProductLineFilter] = useState(""); // BTL | IF
  const [loanPurposeFilter, setLoanPurposeFilter] = useState(""); // BTL only
  const [facilityTypeFilter, setFacilityTypeFilter] = useState(""); // IF only: Confidential | Disclosed
  const [assigneeFilter, setAssigneeFilter] = useState("");
  // Compat aliases (loan purpose was previously named productTypeFilter)
  const productTypeFilter = loanPurposeFilter;
  const setProductTypeFilter = setLoanPurposeFilter;
  void productTypeFilter;
  void setProductTypeFilter;

  // Sorting
  const [sortBy, setSortBy] = useState("modified");
  const [sortOrder, setSortOrder] = useState("desc");

  // Pagination for applications table (client-side) – mirrors ProductManagementPage
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250, 500];
  const ALL_PAGE_SIZE = 1000000; // "All" option

  // Normalize application from backend
  const normalizeApplication = (app) => {
    if (!app) return null;

    // List endpoint returns flat objects, not wrapped in _raw
    const raw = app;

    const applicationPayload = (() => {
      try {
        if (typeof raw.application_payload === "string") {
          return JSON.parse(raw.application_payload);
        }
        return raw.application_payload || {};
      } catch {
        return {};
      }
    })();

    const loanPurpose = raw.loan_purpose || applicationPayload?.loanPurpose || "Purchase";
    const productCategory =
      raw.product_type ||
      raw.product_category ||
      raw.category ||
      applicationPayload?.productType ||
      applicationPayload?.product_category ||
      null;
    const isIfApp = isInvoiceFinanceApplication({
      ...raw,
      productType: productCategory || loanPurpose,
      product_category: productCategory,
      product_type: raw.product_type || productCategory,
      loan_purpose: raw.loan_purpose || applicationPayload?.loan_purpose,
    });
    const productLine = isIfApp ? "IF" : "BTL";
    // Time-to-cash config still keys off loan purpose (Purchase / Remortgage / …)
    const productType = loanPurpose;
    const facilityType = isIfApp
      ? (resolveIfFacilityType({
          ...raw,
          facility_type: raw.facility_type ?? applicationPayload?.facility_type,
          confidential_facility:
            raw.confidential_facility ?? applicationPayload?.confidential_facility,
          confidential: raw.confidential ?? applicationPayload?.confidential,
          disclosure: raw.disclosure ?? applicationPayload?.disclosure,
        }) || "Disclosed")
      : null;
    const rawStatus = raw.status || "Application Submitted";
    const statusLower = String(rawStatus).toLowerCase();
    // IF apps: show "Audit" instead of "Valuation" (backend may still store Valuation)
    const status =
      isIfApp && (statusLower.includes("valuation") || statusLower.includes("audit"))
        ? "Audit"
        : rawStatus;

    // Map status to Time to Cash stages (keep Valuation key for IF so stage config/progress still match)
    let currentStage = "Pre-enquiry";
    if (statusLower.includes("not progress")) currentStage = "Not Progressing";
    else if (statusLower.includes("pre-enquiry") || statusLower.includes("pre enquiry")) currentStage = "Pre-enquiry";
    else if (statusLower.includes("enquiry") && !statusLower.includes("pre")) currentStage = "Enquiry";
    else if (statusLower.includes("application")) currentStage = "Application";
    else if (statusLower.includes("approval")) currentStage = "Approval";
    else if (statusLower.includes("valuation") || statusLower.includes("audit") || status === "Audit")
      currentStage = "Valuation";
    else if (statusLower.includes("legal")) currentStage = "Legal";
    else if (statusLower.includes("completion") || statusLower.includes("completed")) currentStage = "Completion";

    const stageConfig = APPLICATION_STAGES.find(
      (s) => s.value === status || s.value === currentStage || s.label === currentStage
    );

    // Progress based on current stage position in the journey (exclude Not Progressing from progress calc)
    const progressStages = APPLICATION_STAGES.filter((s) => s.value !== "Not Progressing" && s.value !== "Audit");
    const stageIndex = progressStages.findIndex(
      (s) => s.value === (status === "Audit" ? "Valuation" : status) || s.label === currentStage
    );
    const maxIndex = progressStages.length > 1 ? progressStages.length - 1 : 1;
    const progress = stageIndex >= 0 ? Math.round((stageIndex / maxIndex) * 100) : 0;

    const facilityRequired = Number(raw.facility_required ?? applicationPayload?.facility_required ?? 0);
    const loanAmountRaw = Number(raw.loan_amount || applicationPayload?.loanAmount || 0);
    const displayAmount = isIfApp
      ? (facilityRequired > 0 ? facilityRequired : loanAmountRaw)
      : loanAmountRaw;

    return {
      id: raw.name || raw.id || "UNKNOWN",
      name: raw.name || raw.id || "UNKNOWN",
      borrower: {
        name: raw.first_name && raw.surname
          ? `${raw.first_name} ${raw.surname}`
          : applicationPayload?.formData?.firstName && applicationPayload?.formData?.surname
            ? `${applicationPayload.formData.firstName} ${applicationPayload.formData.surname}`
            : raw.applicant || "Unknown",
        email: raw.email || raw.applicant || applicationPayload?.formData?.email || "",
        phone: raw.phone_number || applicationPayload?.formData?.phone || "",
        address: raw.property_address || applicationPayload?.propertyAddress || "",
      },
      productLine,
      productType,
      loanPurpose: isIfApp ? null : loanPurpose,
      facilityType,
      product_category: productCategory || productLine,
      product: {
        productId: raw.product_id || raw.product || applicationPayload?.productId || "—",
        name: isIfApp
          ? formatIfApplicationTitle({
              ...raw,
              facility_type: raw.facility_type ?? applicationPayload?.facility_type,
              confidential_facility:
                raw.confidential_facility ?? applicationPayload?.confidential_facility,
              confidential: raw.confidential ?? applicationPayload?.confidential,
              disclosure: raw.disclosure ?? applicationPayload?.disclosure,
              contract_term_months:
                raw.contract_term_months ??
                applicationPayload?.contract_term_months ??
                applicationPayload?.termMonths,
            })
          : (raw.product_name || applicationPayload?.productName || "—"),
        lender: raw.lender_name || applicationPayload?.lenderName || "—",
        rate: raw.product_rate || applicationPayload?.rate || null,
        rateType: raw.rate_type || applicationPayload?.rateType || "—",
        termMonths: raw.contract_term_months || raw.product_term || applicationPayload?.productTerm || applicationPayload?.termMonths || null,
        fee: raw.product_fee || applicationPayload?.fee || "—",
      },
      loanAmount: displayAmount,
      facilityRequired,
      // LTV comes from backend where available
      ltv: raw.ltv || applicationPayload?.ltv || 0,
      term: raw.mortgage_term || applicationPayload?.mortgageTerm || 0,
      status,
      currentStage,
      statusTone: stageConfig?.color || "blue",
      stageStartDate: raw.stage_start_date || raw.modified || new Date().toISOString().split("T")[0],
      applicationDate: raw.creation || raw.submitted_on || new Date().toISOString(),
      submittedDate: new Date(raw.submitted_on || raw.creation || Date.now()),
      lastUpdated: new Date(raw.modified || raw.creation || Date.now()),
      modified: new Date(raw.modified || raw.creation || Date.now()),
      estimatedClosingDate: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000),
      progress,
      assignedTo: raw.assigned_to || raw.team_member || raw.assign_application || "Unassigned",
      assignedToId: raw.assigned_to_id || null,
      caseManager: raw.case_manager || raw.assigned_to || raw.team_member || raw.assign_application || "Unassigned",
      caseEmail: raw.case_email || "",
      casePhone: raw.case_phone || "",
      documents: {
        total: 8,
        pending: 3,
        verified: 5,
      },
      notes: 0,
      rejectionReason: raw.rejection_reason || "",
      timeline: [],
      _raw: raw,
      _applicationPayload: applicationPayload,
    };
  };

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch applications via list endpoint: GET /api/resource/Applications (response shape: { data: [ { name, ... } ] })
      const appsResponse = await listApplications({
        order_by: "modified desc",
        // High limit so admin diary can see all applications; UI paginates client-side
        limit_page_length: 1000,
        fields: JSON.stringify([
          "name",
          "status",
          "product_type",
          "loan_purpose",
          "loan_amount",
          "facility_required",
          "facility_type",
          "contract_term_months",
          "confidential_facility",
          "confidential",
          "disclosure",
          "first_name",
          "surname",
          "email",
          "phone_number",
          "modified",
          "creation",
          "property_address",
          "product",
          "team_member",
        ]),
      });

      const appsData = Array.isArray(appsResponse) ? appsResponse : [];
      const normalizedApps = appsData
        .map(normalizeApplication)
        .filter(Boolean);

      setApplications(normalizedApps);
      // Reset to first page whenever we refetch the full list
      setCurrentPage(1);

      // Fetch inactive lenders (30+ days)
      const today = new Date();
      const fromDate = new Date(today);
      fromDate.setDate(fromDate.getDate() - 60); // Check last 60 days

      const lendersResponse = await getInactiveLenders({
        from_date: fromDate.toISOString().split("T")[0],
        to_date: today.toISOString().split("T")[0],
        page: 1,
        page_size: 50,
      });

      const lendersData = lendersResponse?.data || [];
      const normalizedLenders = lendersData.map(lender => ({
        id: lender.lender_user || "UNKNOWN",
        name: lender.lender_name || "Unknown Lender",
        contactPerson: lender.lender_user || "Unknown",
        email: lender.lender_user || "",
        phone: "",
        lastLoginDate: lender.last_login ? lender.last_login.split(" ")[0] : "",
        daysSinceLogin: Math.abs(lender.last_login_days_ago || 0),
        activeApplications: lender.active_applications || 0,
        status: lender.activity_status || "Inactive",
        inactiveReason: lender.inactive_reason || "",
      }));

      setLenders(normalizedLenders);

      // Fetch scheduled products for today
      await fetchScheduledProducts();
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch scheduled products for selected date
  const fetchScheduledProducts = async () => {
    try {
      const scheduledResponse = await getMortgageProducts({
        product_schedule: 1,
        limit_page_length: 100,
      });
      const scheduledData = Array.isArray(scheduledResponse) ? scheduledResponse : [];

      // Filter scheduled products by date_time for the selected date only
      const selectedDateStart = new Date(selectedScheduledDate);
      selectedDateStart.setHours(0, 0, 0, 0);
      const selectedDateEnd = new Date(selectedScheduledDate);
      selectedDateEnd.setHours(23, 59, 59, 999);

      const filteredScheduled = scheduledData
        .filter(product => {
          const dateTime = product._raw?.date_time;
          if (!dateTime) return false;

          const scheduleDate = new Date(dateTime);

          // Show only products scheduled for the selected date
          return scheduleDate >= selectedDateStart && scheduleDate <= selectedDateEnd;
        })
        .map(product => {
          const dateTime = product._raw?.date_time;
          const raw = product._raw || product;
          return {
            id: product._id || raw?.name || "SCH-UNKNOWN",
            productId: product._id || raw?.name || "SCH-UNKNOWN",
            name: product.product || raw?.product_name || "Unknown Product",
            lender: raw?.lender_name || raw?.lender || "Unknown Lender",
            productType: raw?.product_type || "—",
            rate: raw?.product_rate_percent || 0,
            goLiveDate: dateTime ? new Date(dateTime).toISOString().split("T")[0] : "",
            goLiveTime: dateTime ? new Date(dateTime).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "",
            changes: "Scheduled product",
            status: "Scheduled",
          };
        })
        .sort((a, b) => {
          const timeA = a.goLiveTime || "";
          const timeB = b.goLiveTime || "";
          return timeA.localeCompare(timeB);
        });

      setScheduledProducts(filteredScheduled);
    } catch (error) {
      console.error("Error fetching scheduled products:", error);
      setScheduledProducts([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Refetch scheduled products when selectedScheduledDate changes
  useEffect(() => {
    fetchScheduledProducts();
  }, [selectedScheduledDate]);

  // Auto-progress applications based on Time to Cash
  useEffect(() => {
    const checkAndProgressApplications = () => {
      setApplications((prevApps) =>
        prevApps.map((app) => {
          const productType = app.productType || "Purchase";
          const config = TIME_TO_CASH_CONFIG[productType] || TIME_TO_CASH_CONFIG.Purchase;
          const currentStage = app.currentStage;
          const daysInStage = calculateDaysSince(app.stageStartDate);
          const idealDaysForStage = config[currentStage] || 0;

          if (daysInStage >= idealDaysForStage && currentStage !== "Completion") {
            const stages = Object.keys(config);
            const currentIndex = stages.indexOf(currentStage);

            if (currentIndex < stages.length - 1) {
              const nextStage = stages[currentIndex + 1];

              return {
                ...app,
                currentStage: nextStage,
                status: nextStage,
                stageStartDate: new Date().toISOString().split("T")[0],
                lastUpdated: new Date(),
                modified: new Date(),
              };
            }
          }

          return app;
        })
      );
    };

    const interval = setInterval(checkAndProgressApplications, 60000);
    return () => clearInterval(interval);
  }, []);

  // Navigation
  const goToPreviousDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = () => {
    const today = new Date();
    return (
      currentDate.getDate() === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const formatDateHeader = (date) => {
    return date.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Filtered applications
  const filteredApplications = useMemo(() => {
    let filtered = [...applications];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (app) =>
          app.id.toLowerCase().includes(search) ||
          app.borrower.name.toLowerCase().includes(search) ||
          (app.productLine || "").toLowerCase().includes(search) ||
          (app.loanPurpose || app.productType || "").toLowerCase().includes(search) ||
          (app.facilityType || "").toLowerCase().includes(search) ||
          (app.product?.name || "").toLowerCase().includes(search) ||
          (app.product?.productId || "").toLowerCase().includes(search) ||
          (app.product?.lender || "").toLowerCase().includes(search)
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((app) => {
        if (statusFilter === "Valuation") {
          // IF apps display "Audit" for the valuation stage
          return app.status === "Valuation" || app.status === "Audit";
        }
        if (statusFilter === "Audit") {
          return app.status === "Audit" || app.status === "Valuation";
        }
        return app.status === statusFilter;
      });
    }
    if (productLineFilter) {
      filtered = filtered.filter((app) => (app.productLine || "BTL") === productLineFilter);
    }
    // Loan purpose applies to BTL only
    if (loanPurposeFilter) {
      filtered = filtered.filter(
        (app) =>
          (app.productLine || "BTL") === "BTL" &&
          (app.loanPurpose || app.productType) === loanPurposeFilter
      );
    }
    // Facility type applies to IF only (Confidential / Disclosed)
    if (facilityTypeFilter) {
      filtered = filtered.filter(
        (app) =>
          app.productLine === "IF" &&
          (app.facilityType || "").toLowerCase() === facilityTypeFilter.toLowerCase()
      );
    }
    if (assigneeFilter) filtered = filtered.filter((app) => app.assignedTo === assigneeFilter);

    filtered.sort((a, b) => {
      let compareA, compareB;
      switch (sortBy) {
        case "amount":
          compareA = Number(a.loanAmount) || 0;
          compareB = Number(b.loanAmount) || 0;
          break;
        case "borrower":
          compareA = a.borrower.name;
          compareB = b.borrower.name;
          break;
        case "productLine":
          compareA = a.productLine || "";
          compareB = b.productLine || "";
          break;
        case "applicationType":
          compareA = getApplicationTypeSortKey(a);
          compareB = getApplicationTypeSortKey(b);
          break;
        case "loanPurpose":
          compareA = a.loanPurpose || a.productType || "";
          compareB = b.loanPurpose || b.productType || "";
          break;
        case "facility":
          compareA = a.facilityType || "";
          compareB = b.facilityType || "";
          break;
        case "modified":
        default: {
          // For default sorting, use the most recent of submitted or updated date
          const aRecentDate = new Date(Math.max(
            (a.submittedDate ? new Date(a.submittedDate).getTime() : 0),
            (a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0),
            (a.modified ? new Date(a.modified).getTime() : 0)
          ));
          const bRecentDate = new Date(Math.max(
            (b.submittedDate ? new Date(b.submittedDate).getTime() : 0),
            (b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0),
            (b.modified ? new Date(b.modified).getTime() : 0)
          ));
          compareA = aRecentDate;
          compareB = bRecentDate;
          break;
        }
      }

      if (typeof compareA === "string" && typeof compareB === "string") {
        const cmp = compareA.localeCompare(compareB, undefined, { sensitivity: "base" });
        if (sortOrder === "asc") return cmp;
        return -cmp;
      }

      if (sortOrder === "asc") return compareA > compareB ? 1 : -1;
      return compareA < compareB ? 1 : -1;
    });

    return filtered;
  }, [applications, searchTerm, statusFilter, productLineFilter, loanPurposeFilter, facilityTypeFilter, assigneeFilter, sortBy, sortOrder]);

  // Clamp current page when filters change
  useEffect(() => {
    const pageSize = itemsPerPage >= (ALL_PAGE_SIZE || 1000000) ? filteredApplications.length || 1 : itemsPerPage;
    const totalPages = Math.max(1, Math.ceil(filteredApplications.length / pageSize));
    setCurrentPage((prev) => {
      if (!prev || prev < 1) return 1;
      if (prev > totalPages) return totalPages;
      return prev;
    });
  }, [filteredApplications.length, itemsPerPage]);

  const paginatedApplications = useMemo(() => {
    const pageSize = itemsPerPage >= (ALL_PAGE_SIZE || 1000000) ? filteredApplications.length || 1 : itemsPerPage;
    const start = (currentPage - 1) * pageSize;
    return filteredApplications.slice(start, start + pageSize);
  }, [filteredApplications, currentPage, itemsPerPage]);

  // Inactive lenders (30+ days)
  const inactiveLenders = useMemo(() => {
    return lenders.filter((lender) => lender.daysSinceLogin >= 30);
  }, [lenders]);

  // Today's scheduled products (already filtered by fetchScheduledProducts)
  const todaysProducts = useMemo(() => {
    return scheduledProducts;
  }, [scheduledProducts]);

  // Actions
  const handleSelectAll = () => {
    if (selectedIds.size === filteredApplications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredApplications.map((app) => app.id)));
    }
  };

  const handleSelectOne = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedIds(newSelected);
  };

  const handleDeleteApplication = async (application) => {
    if (!application || !application.id) return;
    try {
      await deleteApplication(application.id);
      setApplications((prev) => prev.filter((app) => app.id !== application.id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(application.id);
        return next;
      });
      setDeleteApp(null);
    } catch (error) {
      console.error("Error deleting application:", error);
      alert("Failed to delete application. Please try again.");
    }
  };

  const handleUpdateStatus = async (id, newStatus, note) => {
    try {
      const now = new Date();
      const stageDateField = STATUS_TO_STAGE_DATE_FIELD[newStatus];
      const statusChangedOn = now.toISOString().slice(0, 19).replace("T", " ");
      const updatePayload = {
        status: newStatus,
        stage_start_date: now.toISOString().split("T")[0],
        status_changed_on: statusChangedOn,
      };
      if (stageDateField) {
        updatePayload[stageDateField] = statusChangedOn;
      }

      // Update in backend
      await updateApplication(id, updatePayload);

      // Update local state
      setApplications((prev) =>
        prev.map((app) => {
          if (app.id === id) {
            const statusConfig = APPLICATION_STAGES.find((s) => s.value === newStatus);
            const isIf = isInvoiceFinanceApplication(app);
            const displayStatus =
              isIf && newStatus === "Valuation" ? "Audit" : newStatus;
            // Keep Valuation for IF so Time-to-Cash config keys still resolve
            const stageKey =
              newStatus === "Legals"
                ? "Legal"
                : newStatus === "Pre Enquiry"
                  ? "Pre-enquiry"
                  : newStatus === "Audit"
                    ? "Valuation"
                    : newStatus;
            const updatedRaw = {
              ...(app._raw || {}),
              status: newStatus,
              status_changed_on: statusChangedOn,
              stage_start_date: now.toISOString().split("T")[0],
              modified: statusChangedOn,
              ...(stageDateField ? { [stageDateField]: statusChangedOn } : {}),
            };

            return {
              ...app,
              status: displayStatus,
              currentStage: stageKey,
              statusTone: statusConfig?.color || "blue",
              stageStartDate: new Date().toISOString().split("T")[0],
              lastUpdated: new Date(),
              modified: new Date(),
              _raw: updatedRaw,
              activity: getActivities(
                {
                  status: displayStatus,
                  submitted_on: app.submitted_on || app.submittedDate || updatedRaw.creation,
                  modified: statusChangedOn,
                },
                updatedRaw
              ),
              timeline: [
                ...app.timeline,
                {
                  id: app.timeline.length + 1,
                  status: displayStatus,
                  timestamp: new Date(),
                  user: "Admin",
                  note: note || `Status changed to ${displayStatus}`,
                  type: "status_change",
                },
              ],
            };
          }
          return app;
        })
      );
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status. Please try again.");
    }
  };

  const handleAssign = async (id, member) => {
    try {
      // Assign team member via dedicated backend assignment endpoint.
      await api.post(
        "/api/method/dynesourcerapi.utils.application_assignment.assign_team_member",
        {
          application_name: id,
          team_member: member.name,
          team_member_email: member.email,
        }
      );

      // Keep existing application fields in sync for UI display/filtering.
      await updateApplication(id, {
        team_member: member.name,
        team_member_email: member.email,
        assign_application: member.name,
        assigned_to: member.email,
        assigned_to_id: member.id,
        case_manager: member.name,
        case_email: member.email,
      });

      // Update local state
      setApplications((prev) =>
        prev.map((app) => {
          if (app.id === id) {
            return {
              ...app,
              assignedTo: member.name,
              assignedToId: member.id,
              caseManager: member.name,
              caseEmail: member.email,
              lastUpdated: new Date(),
              modified: new Date(),
              timeline: [
                ...app.timeline,
                {
                  id: app.timeline.length + 1,
                  status: app.status,
                  timestamp: new Date(),
                  user: "Admin",
                  note: `Application assigned to ${member.name}`,
                  type: "assignment",
                },
              ],
            };
          }
          return app;
        })
      );
    } catch (error) {
      console.error("Error assigning application:", error);
      alert("Failed to assign application. Please try again.");
    }
  };

  // Helper function to fetch product by ID — Mortgage Product or Invoice Finance by id prefix
  const getProductById = async (productId) => {
    if (!productId) return null;
    const encoded = encodeURIComponent(productId);
    const isIfId = String(productId).toUpperCase().startsWith("IF-");
    try {
      if (isIfId) {
        const res = await api.get(`/api/resource/${encodeURIComponent("Invoice Finance")}/${encoded}`);
        return res?.data?.data || res?.data || null;
      }
      const res = await api.get(`/api/resource/Mortgage Product/${encoded}`);
      return res?.data?.data || res?.data || null;
    } catch (error) {
      console.warn("Failed to fetch product:", productId, error);
      return null;
    }
  };

  const fetchIfEorSearchResults = async ({
    applicationPayload,
    payloadForIllustration,
    fullApp,
  }) => {
    try {
      const sourcingInputs = applicationPayload?.meta?.sourcingInputs || {};
      const filtersFromPayload =
        applicationPayload?.meta?.filters ||
        applicationPayload?.filters ||
        sourcingInputs?.filters ||
        {};
      // Normalize camelCase filter keys from IF sourcing UI into snake_case API shape.
      const filters = {
        creditWorthiness:
          filtersFromPayload.creditWorthiness ?? filtersFromPayload.credit_worthiness,
        creditTermsDays:
          filtersFromPayload.creditTermsDays ?? filtersFromPayload.credit_terms_days,
        contractTermMonths:
          filtersFromPayload.contractTermMonths ??
          filtersFromPayload.contract_term_months ??
          fullApp?.contract_term_months ??
          payloadForIllustration?.contract_term_months,
        contractualDebtAgreements: Boolean(
          filtersFromPayload.contractualDebtAgreements ??
            filtersFromPayload.contractual_debt_agreements
        ),
        proofOfDebt: Boolean(
          filtersFromPayload.proofOfDebt ?? filtersFromPayload.proof_of_debt
        ),
        currentShortTermDebt: Boolean(
          filtersFromPayload.currentShortTermDebt ??
            filtersFromPayload.current_short_term_debt
        ),
        selectiveInvoicing: Boolean(
          filtersFromPayload.selectiveInvoicing ?? filtersFromPayload.selective_invoicing
        ),
        debtorProtection: Boolean(
          filtersFromPayload.debtorProtection ?? filtersFromPayload.debtor_protection
        ),
        fundingLedgerType:
          filtersFromPayload.fundingLedgerType ?? filtersFromPayload.funding_ledger_type,
        hmrcArrears: Boolean(
          filtersFromPayload.hmrcArrears ?? filtersFromPayload.hmrc_arrears
        ),
        previousLiquidatedCo: Boolean(
          filtersFromPayload.previousLiquidatedCo ??
            filtersFromPayload.previous_liquidated_co
        ),
        directorExperience: Boolean(
          filtersFromPayload.directorExperience ?? filtersFromPayload.director_experience
        ),
        personalGuarantees: Boolean(
          filtersFromPayload.personalGuarantees ?? filtersFromPayload.personal_guarantees
        ),
        confidentialFacility: Boolean(
          filtersFromPayload.confidentialFacility ??
            filtersFromPayload.confidential_facility ??
            fullApp?.confidential_facility ??
            payloadForIllustration?.confidential_facility
        ),
        concentrationLimit:
          filtersFromPayload.concentrationLimit ?? filtersFromPayload.concentration_limit,
        exportLedger: filtersFromPayload.exportLedger ?? filtersFromPayload.export_ledger,
        homeOwner: filtersFromPayload.homeOwner ?? filtersFromPayload.home_owner,
      };

      const firstPositive = (...vals) => {
        for (const v of vals) {
          const n = Number(v);
          if (Number.isFinite(n) && n > 0) return n;
        }
        return 0;
      };

      const sector =
        String(
          fullApp?.sector ||
            payloadForIllustration?.sector ||
            applicationPayload?.sector ||
            sourcingInputs?.sector ||
            ""
        ).trim() || undefined;

      const facilityNum = firstPositive(
        fullApp?.facility_required,
        payloadForIllustration?.facility_required,
        applicationPayload?.facility_required,
        sourcingInputs?.facilityRequired,
        sourcingInputs?.facility_required,
        fullApp?.loan_amount,
        applicationPayload?.loan_amount
      );
      const turnoverNum = firstPositive(
        fullApp?.projected_turnover,
        applicationPayload?.projected_turnover,
        sourcingInputs?.projectedTurnover,
        sourcingInputs?.projected_turnover
      );
      const abTypeRaw =
        sourcingInputs?.additionalBorrowingType ||
        applicationPayload?.additional_borrowing_type ||
        "amount";
      const additionalBorrowingType =
        String(abTypeRaw).toLowerCase() === "percent" || String(abTypeRaw) === "%"
          ? "percent"
          : "amount";
      const additionalBorrowingNum = firstPositive(
        fullApp?.additional_borrowing_amount,
        applicationPayload?.additional_borrowing_amount,
        sourcingInputs?.additionalBorrowingValue,
        sourcingInputs?.additional_borrowing
      );

      const payload = buildInvoiceFinanceSourcingPayload({
        sector,
        facilityNum,
        turnoverNum,
        additionalBorrowingType,
        additionalBorrowingNum,
        filters,
      });

      const res = await getInvoiceFinanceSourcingFilters(payload);
      const rawProducts = res?.products || [];
      const afterSector = rawProducts.filter(
        (p) =>
          !isExcludedInvoiceFinanceSector(p?.sector) &&
          (!sector || matchesInvoiceFinanceSector(p?.sector, sector))
      );
      const catalogueProducts = filterInvoiceFinanceSourcingProducts(afterSector);
      const abValue =
        additionalBorrowingNum > 0
          ? String(additionalBorrowingNum)
          : "";
      const abType = additionalBorrowingType === "percent" ? "%" : "£";
      const mapped = mapSourcingApiProducts(catalogueProducts, {
        facilityNum,
        turnoverNum,
        abValue,
        abType,
      });
      const eligible = mapped.filter((row) =>
        productMatchesInvoiceEligibility(row, filters)
      );
      const matched =
        facilityNum > 0
          ? eligible.filter((row) => productMatchesFacilityBand(row, facilityNum))
          : eligible;

      // Prefer eligible band matches; fall back to wider mapped pool so EOR still has rows.
      const pool = matched.length > 0 ? matched : mapped.length > 0 ? mapped : catalogueProducts;

      const rows = pool.map((row) => {
        const raw = row?._raw || row?.sourceProduct || row || {};
        return {
          ...row,
          name: row?.id || row?.name || raw?.name,
          product_id: row?.id || row?.name || raw?.name,
          product_name:
            row?.product_name ||
            row?.productName ||
            raw?.product_name ||
            row?.id ||
            row?.name,
          lender_name:
            row?.lenderName ||
            row?.lender_name ||
            raw?.lender_name ||
            raw?.lender_product_code ||
            "",
          apr: row?.ratePct ?? row?.apr ?? raw?.apr,
          ratePct: row?.ratePct,
          discount_rate_percent: row?.ratePct ?? raw?.discount_rate_percent,
          ip_percent: row?.invoicePurchasePct ?? raw?.ip_percent,
          invoicePurchasePct: row?.invoicePurchasePct,
          service_fee_percent: row?.annualCostPct ?? raw?.service_fee_percent,
          annualCostPct: row?.annualCostPct,
          termMonths: row?.termMonths,
          contract_term_months: row?.termMonths ?? raw?.contract_term_months,
          net_advance: row?.netAdvance ?? raw?.net_advance,
          netAdvance: row?.netAdvance,
          cost_over_product_term: row?.costOverProductTerm ?? raw?.cost_over_product_term,
          costOverProductTerm: row?.costOverProductTerm,
          arrangement_fee: raw?.arrangement_fee,
        };
      });

      const facilityType =
        resolveInvoiceFinanceFacilityType({
          ...payloadForIllustration,
          ...fullApp,
          confidential_facility: filters.confidentialFacility ? 1 : 0,
        }) || payloadForIllustration?.facility_type || "—";

      const appliedFilters = {
        sector: sector || "—",
        facility_required: facilityNum,
        projected_turnover: turnoverNum,
        contract_term_months:
          filters.contractTermMonths ||
          payloadForIllustration?.contract_term_months ||
          "—",
        facility_type: facilityType,
        filters: payload.filters || {},
      };

      return { rows, appliedFilters };
    } catch (error) {
      console.warn("Failed to fetch IF EOR search results:", error);
      return { rows: [], appliedFilters: {} };
    }
  };

  const fetchEorSearchResults = async ({
    applicationPayload,
    payloadForIllustration,
    applicationLoanAmount,
    applicationPropertyValue,
    applicationDepositAmount,
    applicationEstRent,
  }) => {
    try {
      const sourcingInputs = applicationPayload?.meta?.sourcingInputs || {};
      const firstPositive = (...vals) => {
        for (const v of vals) {
          const n = Number(v);
          if (Number.isFinite(n) && n > 0) return n;
        }
        return null;
      };
      const resolvedPropertyValue =
        firstPositive(
          applicationPropertyValue,
          applicationPayload?.property_value,
          applicationPayload?.propertyValue,
          sourcingInputs?.propertyValue,
          sourcingInputs?.property_value
        ) ?? 0;
      const resolvedMortgageAmount =
        firstPositive(
          applicationLoanAmount,
          applicationPayload?.loan_amount,
          applicationPayload?.mortgageAmount,
          sourcingInputs?.mortgageAmount,
          sourcingInputs?.loanAmount
        ) ?? 0;
      const resolvedDepositAmount =
        firstPositive(
          applicationDepositAmount,
          applicationPayload?.deposit_amount,
          applicationPayload?.depositAmount,
          sourcingInputs?.depositAmount,
          sourcingInputs?.deposit_amount
        ) ??
        ((resolvedPropertyValue > 0 && resolvedMortgageAmount > 0)
          ? Math.max(0, resolvedPropertyValue - resolvedMortgageAmount)
          : 0);
      const resolvedEstimatedRent =
        firstPositive(
          applicationEstRent,
          applicationPayload?.estRent,
          applicationPayload?.est_rent,
          applicationPayload?.estimated_rent,
          applicationPayload?.estimatedRent,
          sourcingInputs?.estRent,
          sourcingInputs?.estimatedRent,
          sourcingInputs?.estimated_rent,
          sourcingInputs?.estRent,
          sourcingInputs?.est_rent
        ) ?? 0;

      // Helps debug when EOR is showing 0 deposit/rent despite shortlist/app having values.
      if (process.env.NODE_ENV === "development") {
        console.log("🧾 [Admin EOR] rent resolution inputs:", {
          applicationEstRent,
          applicationPayload_estRent: applicationPayload?.estRent,
          applicationPayload_est_rent: applicationPayload?.est_rent,
          applicationPayload_estimated_rent: applicationPayload?.estimated_rent,
          applicationPayload_estimatedRent: applicationPayload?.estimatedRent,
          sourcingInputs_estRent: sourcingInputs?.estRent,
          sourcingInputs_est_rent: sourcingInputs?.est_rent,
          resolvedEstimatedRent,
        });
      }

      // Match borrower sourcing: request a large pool so we can rank and take top 50.
      const EOR_FETCH_LIMIT = 100000;
      const requestBody = {
        property_value: resolvedPropertyValue,
        mortgage_amount: resolvedMortgageAmount,
        estimated_rent: resolvedEstimatedRent,
        deposit_amount: resolvedDepositAmount,
        mortgage_term_years:
          applicationPayload?.mortgage_term_years ??
          applicationPayload?.mortgageTerm ??
          sourcingInputs?.mortgage_term_years ??
          sourcingInputs?.mortgageTerm ??
          25,
        repaymentType: payloadForIllustration?.repaymentType || "Repayment",
        filters:
          applicationPayload?.filters ||
          applicationPayload?.meta?.filters ||
          sourcingInputs?.filters ||
          {},
        limit: EOR_FETCH_LIMIT,
        limit_page_length: EOR_FETCH_LIMIT,
      };
      const res = await api.post("/api/method/dynesourcerapi.calculate.get_mortgage_products_details", requestBody);
      const msg = res?.data?.message || {};
      // Same response-shape handling as calculateMortgageProducts (sourcing).
      const rows = Array.isArray(msg?.data)
        ? msg.data
        : Array.isArray(msg?.results)
          ? msg.results
          : Array.isArray(msg?.products)
            ? msg.products
            : [];
      const appliedFilters = {
        property_value: requestBody.property_value,
        deposit_amount: requestBody.deposit_amount,
        mortgage_amount: requestBody.mortgage_amount,
        estimated_rent: requestBody.estimated_rent,
        mortgage_term_years: requestBody.mortgage_term_years,
        repayment_type: requestBody.repaymentType,
        filters: requestBody.filters || {},
      };
      return { rows, appliedFilters };
    } catch (error) {
      console.warn("Failed to fetch EOR search results:", error);
      return { rows: [], appliedFilters: {} };
    }
  };

  const lenderUserDisplayName = (u) =>
    String(u?.full_name || [u?.first_name, u?.last_name].filter(Boolean).join(" ") || u?.email || "").trim();

  const fetchLenderUsers = async () => {
    try {
      const allRows = [];
      let limitStart = 0;
      const pageSize = 200;
      let totalCount = null;

      while (totalCount == null || limitStart < totalCount) {
        const res = await api.get("/api/method/dynesourcerapi.user_directory.admin_list_users", {
          params: {
            limit_start: limitStart,
            limit_page_length: pageSize,
            role: "Lender",
            exclude_system_manager: 1,
            search: "",
          },
        });
        const msg = res?.data?.message || {};
        const rows = Array.isArray(msg?.data) ? msg.data : [];
        totalCount = Number(msg?.total_count ?? rows.length ?? 0);
        allRows.push(...rows);
        if (rows.length === 0) break;
        limitStart += pageSize;
      }
      return allRows;
    } catch (error) {
      console.warn("Failed to fetch lender names:", error);
      return [];
    }
  };

  const toSortedLenderNames = (lenderUsers) => {
    const names = lenderUsers.map(lenderUserDisplayName).filter(Boolean);
    return Array.from(new Set(names)).sort((a, b) => String(a).localeCompare(String(b)));
  };

  // IF sourcing rows carry no lender field; the lender is the Invoice Finance doc owner.
  const applyIfLenderNames = async (rows, lenderUsers) => {
    const ids = Array.from(
      new Set(rows.map((row) => String(row?.name || row?.product_id || row?.id || "").trim()).filter(Boolean))
    );
    if (ids.length === 0) return rows;

    const ownerById = new Map();
    try {
      const res = await api.get(`/api/resource/${encodeURIComponent("Invoice Finance")}`, {
        params: {
          fields: JSON.stringify(["name", "owner"]),
          filters: JSON.stringify([["name", "in", ids]]),
          limit_page_length: ids.length,
        },
      });
      const docs = Array.isArray(res?.data?.data) ? res.data.data : [];
      docs.forEach((d) => {
        if (d?.name && d?.owner) ownerById.set(String(d.name).trim().toLowerCase(), String(d.owner).trim());
      });
    } catch (error) {
      console.warn("Failed to fetch IF product owners for EOR:", error);
    }

    const nameByEmail = new Map();
    lenderUsers.forEach((u) => {
      const email = String(u?.email || u?.name || "").trim().toLowerCase();
      const display = lenderUserDisplayName(u);
      if (email && display) nameByEmail.set(email, display);
    });

    return rows.map((row) => {
      const id = String(row?.name || row?.product_id || row?.id || "").trim().toLowerCase();
      const owner = ownerById.get(id) || String(row?.owner || "").trim();
      if (!owner) return row;
      const lenderName = nameByEmail.get(owner.toLowerCase()) || owner;
      return { ...row, lender_name: lenderName, lenderName };
    });
  };

  // Handle ESIS/EOR download - use full application doc (same as borrower portal) so illustration matches exactly
  const isGeneratingESISRef = useRef(false);
  const handleDownloadESIS = async (application, mode = "esis") => {
    if (isGeneratingESISRef.current) return;

    isGeneratingESISRef.current = true;
    try {
      // Fetch full application doc (same as borrower portal) so we have application_payload, repayment_type, cost_over_product_term, etc.
      const fullApp = await getApplication(application.id || application.name);
      if (!fullApp) {
        alert("Could not load application details. Please try again.");
        return;
      }

      if (process.env.NODE_ENV === "development") {
        const rentKeysFullApp = Object.keys(fullApp || {}).filter((k) => {
          const lk = String(k).toLowerCase();
          return lk.includes("rent") || lk.includes("rental");
        });
        console.log("🧾 [Admin EOR] fullApp rent snapshot:", {
          rentKeysFullApp,
          fullApp_est_rent: fullApp?.est_rent,
          fullApp_estRent: fullApp?.estRent,
          fullApp_estimated_rent: fullApp?.estimated_rent,
          fullApp_estimatedRent: fullApp?.estimatedRent,
        });
      }

      const applicationPayload = (() => {
        try {
          if (typeof fullApp.application_payload === "string") {
            return JSON.parse(fullApp.application_payload);
          }
          return fullApp.application_payload || {};
        } catch {
          return {};
        }
      })();

      if (process.env.NODE_ENV === "development") {
        const rentKeys = Object.keys(applicationPayload || {}).filter((k) => {
          const lk = String(k).toLowerCase();
          return lk.includes("rent");
        });
        const sourcingRentKeys = Object.keys(applicationPayload?.meta?.sourcingInputs || {}).filter((k) => {
          const lk = String(k).toLowerCase();
          return lk.includes("rent") || lk.includes("rental");
        });
        console.log("🧾 [Admin EOR] applicationPayload rent snapshot:", {
          rentKeys,
          sourcingRentKeys,
          applicationPayload_rent_values: {
            est_rent: applicationPayload?.est_rent,
            estimated_rent: applicationPayload?.estimated_rent,
            estRent: applicationPayload?.estRent,
            estimatedRent: applicationPayload?.estimatedRent,
          },
        });
      }

      // Get shortlisted products for matching/enrichment.
      // In admin portal this call can fail with "User not authenticated",
      // so prefer payload shortlist and treat API fetch as best-effort.
      let shortlistedData = Array.isArray(applicationPayload?.meta?.shortlistedProducts)
        ? applicationPayload.meta.shortlistedProducts
        : [];
      try {
        const shortlistedResponse = await getShortlistedProducts(application.borrower.email);
        if (shortlistedResponse?.success && Array.isArray(shortlistedResponse.data)) {
          shortlistedData = shortlistedResponse.data;
        } else if (shortlistedResponse?.message?.items && Array.isArray(shortlistedResponse.message.items)) {
          shortlistedData = shortlistedResponse.message.items;
        } else if (Array.isArray(shortlistedResponse?.message)) {
          shortlistedData = shortlistedResponse.message;
        } else if (Array.isArray(shortlistedResponse?.data)) {
          shortlistedData = shortlistedResponse.data;
        } else if (Array.isArray(shortlistedResponse)) {
          shortlistedData = shortlistedResponse;
        }
      } catch (err) {
        console.warn("Shortlist fetch skipped in admin EOR flow:", err?.message || err);
      }

      const effectiveProductId =
        fullApp.product ||
        application._raw?.product ||
        application.product?.productId ||
        applicationPayload?.productId ||
        applicationPayload?.product_id;

      const matchingShortlist = shortlistedData.find(item => {
        const itemProductId = String(item.mortgage_product || item.product_id || item.name || "").trim();
        const itemApplicationId = String(item.application_id || item.application || item.application_name || "").trim();
        const targetProductId = String(effectiveProductId || "").trim();
        const targetId = String(application.id || application.name || "").trim();

        return (
          (targetProductId && itemProductId === targetProductId) ||
          (targetProductId && String(item.name || "").trim() === targetProductId) ||
          (targetId && itemApplicationId === targetId)
        );
      });

      const productId = effectiveProductId;

      let productData = null;
      if (productId) {
        productData = await getProductById(productId);
      }
      if (!productData) {
        // Mirror borrower portal fallback so IF ESIS still gets Product ID + fees from the application
        const raw = fullApp || application._raw || {};
        productData = {
          name: productId || application.id,
          product_id: productId || application.id,
          id: productId || application.id,
          product_name: productId || application.id,
          product_category: raw.product_type || applicationPayload?.product_type || "IF",
          category: raw.product_type || applicationPayload?.product_type || "IF",
          productType: raw.product_type || applicationPayload?.product_type || "IF",
          sector: raw.sector ?? applicationPayload?.sector,
          facility_required: raw.facility_required ?? applicationPayload?.facility_required,
          facility_type: raw.facility_type ?? applicationPayload?.facility_type,
          net_advance: raw.net_advance ?? applicationPayload?.net_advance,
          contract_term_months: raw.contract_term_months ?? applicationPayload?.contract_term_months,
          termMonths: raw.contract_term_months ?? applicationPayload?.contract_term_months,
          min_service_fee: raw.min_service_fee ?? applicationPayload?.min_service_fee,
          ip_percent: raw.ip_percent ?? applicationPayload?.ip_percent,
          service_fee_percent:
            raw.service_fee_percent ??
            applicationPayload?.service_fee_percent ??
            raw.simple_service_fee ??
            applicationPayload?.simple_service_fee,
          annualCostPct:
            raw.service_fee_percent ??
            applicationPayload?.service_fee_percent ??
            raw.simple_service_fee ??
            applicationPayload?.simple_service_fee,
          simple_service_fee: raw.simple_service_fee ?? applicationPayload?.simple_service_fee,
          discount_rate_percent: raw.discount_rate_percent ?? applicationPayload?.discount_rate_percent,
          apr: raw.apr ?? applicationPayload?.apr,
          arrangement_fee: raw.arrangement_fee ?? applicationPayload?.arrangement_fee,
          cost_over_product_term: raw.cost_over_product_term ?? applicationPayload?.cost_over_product_term,
          additional_borrowing_amount: raw.additional_borrowing_amount ?? applicationPayload?.additional_borrowing_amount,
          interest_rate: raw.interest_rate ?? applicationPayload?.interest_rate,
          first_12_month_interest: raw.first_12_month_interest ?? applicationPayload?.first_12_month_interest,
          monthly_repayments: raw.monthly_repayments ?? applicationPayload?.monthly_repayments,
          total_repayable: raw.total_repayable ?? applicationPayload?.total_repayable,
          confidential_facility: raw.confidential_facility ?? applicationPayload?.confidential_facility,
          disclosure: raw.disclosure ?? applicationPayload?.disclosure,
          loan_amount: raw.loan_amount,
          loan_purpose: raw.loan_purpose ?? applicationPayload?.loan_purpose,
          ab_arrangement_fee: raw.ab_arrangement_fee ?? applicationPayload?.ab_arrangement_fee,
          ab_cost_over_product_term:
            raw.ab_cost_over_product_term ??
            applicationPayload?.ab_cost_over_product_term ??
            raw.ab_cost_over_term ??
            applicationPayload?.ab_cost_over_term,
          abCalc: applicationPayload?.abCalc || null,
        };
      }

      const safeNum = (v, fallback = 0) => {
        if (v === null || v === undefined || v === "") return fallback;
        if (typeof v === "string") {
          const s = v.replace(/[£,%\s]/g, "");
          const n = Number(s);
          return Number.isFinite(n) ? n : fallback;
        }
        const n = Number(v);
        return Number.isFinite(n) ? n : fallback;
      };

      // Loan amount and property value: same priority as borrower portal (shortlist first, then full doc / payload)
      let loanAmountRaw = 0;
      let propertyValueRaw = 0;
      let depositAmountRaw = null;
      let estRentRaw = null;

      const pickFirstPositiveRentFromObj = (obj) => {
        if (!obj || typeof obj !== "object") return null;
        for (const k of Object.keys(obj)) {
          const lk = String(k).toLowerCase();
          if (!lk.includes("rent") && !lk.includes("rental")) continue;
          const n = safeNum(obj[k], null);
          if (n !== null && Number.isFinite(n) && n > 0) return n;
        }
        return null;
      };
      if (matchingShortlist) {
        loanAmountRaw = safeNum(
          matchingShortlist.loan_amount ||
          matchingShortlist.mortgageAmount ||
          matchingShortlist.mortgage_amount ||
          0
        );
        propertyValueRaw = safeNum(
          matchingShortlist.property_value ||
          matchingShortlist.propertyValue ||
          0
        );
        const depositCandidate =
          matchingShortlist.deposit_amount ??
          matchingShortlist.depositAmount;
        if (depositCandidate !== null && depositCandidate !== undefined && depositCandidate !== "") {
          depositAmountRaw = safeNum(depositCandidate, null);
        }
        const rentCandidate =
          matchingShortlist.est_rent ??
          matchingShortlist.estimated_rent ??
          matchingShortlist.estimatedRent ??
          matchingShortlist.estRent;
        if (rentCandidate !== null && rentCandidate !== undefined && rentCandidate !== "") {
          estRentRaw = safeNum(rentCandidate, null);
        }
      }
      if (estRentRaw == null) {
        // As a last resort, scan for any rent-ish key in the shortlist item.
        estRentRaw = pickFirstPositiveRentFromObj(matchingShortlist);
      }
      if (estRentRaw == null) {
        // Or scan the application's sourcingInputs blob.
        const sourcingInputs = applicationPayload?.meta?.sourcingInputs || {};
        estRentRaw = pickFirstPositiveRentFromObj(sourcingInputs);
      }

      // Fallback to top-level fields on the Application doc (fullApp) — these are
      // stored directly by add_to_applicant_record and returned by GET /api/resource/Applications/:name
      if ((depositAmountRaw == null || depositAmountRaw === 0) && fullApp) {
        const fromDoc = safeNum(fullApp.deposit_amount ?? fullApp.depositAmount, 0);
        if (fromDoc > 0) depositAmountRaw = fromDoc;
      }
      if (estRentRaw == null && fullApp) {
        const fromDoc = safeNum(
          fullApp.est_rent ?? fullApp.estimated_rent ?? fullApp.estimatedRent ?? fullApp.estRent,
          0
        );
        if (fromDoc > 0) estRentRaw = fromDoc;
      }

      if (loanAmountRaw === 0) {
        loanAmountRaw = safeNum(
          fullApp.loan_amount ??
          fullApp.mortgage_amount ??
          applicationPayload?.loanAmount ??
          applicationPayload?.loan_amount ??
          0
        );
      }
      if (propertyValueRaw === 0) {
        propertyValueRaw = safeNum(
          fullApp.property_value ??
          applicationPayload?.propertyValue ??
          applicationPayload?.property_value ??
          0
        );
      }

      const applicationLoanAmount = loanAmountRaw > 0 ? loanAmountRaw : null;
      const applicationPropertyValue = propertyValueRaw > 0 ? propertyValueRaw : null;
      const derivedDepositAmount =
        applicationPropertyValue != null && applicationLoanAmount != null
          ? Math.max(0, applicationPropertyValue - applicationLoanAmount)
          : null;
      const applicationDepositAmount =
        depositAmountRaw !== null && Number.isFinite(depositAmountRaw) && depositAmountRaw > 0
          ? depositAmountRaw
          : derivedDepositAmount;
      const applicationEstRent =
        estRentRaw !== null && Number.isFinite(estRentRaw) && estRentRaw > 0 ? estRentRaw : null;

      if (process.env.NODE_ENV === "development") {
        console.log("🧩 [Admin EOR] rent raw resolution:", {
          estRentRaw,
          matchingShortlist_est_rent: matchingShortlist?.est_rent,
          matchingShortlist_est_rent_alt: matchingShortlist?.estimated_rent,
          matchingShortlist_estRent: matchingShortlist?.estRent,
          matchingShortlist_estimatedRent: matchingShortlist?.estimatedRent,
          matchingShortlist_estRentAlt: matchingShortlist?.estimatedRentAmount,
          applicationPayload_est_rent: applicationPayload?.est_rent,
          applicationPayload_estRent: applicationPayload?.estRent,
          applicationPayload_estimated_rent: applicationPayload?.estimated_rent,
          applicationPayload_estimatedRent: applicationPayload?.estimatedRent,
          matchingShortlistRentKeys: Object.keys(matchingShortlist || {})
            .filter((k) => String(k).toLowerCase().includes("rent")),
          applicationPayloadRentKeys: Object.keys(applicationPayload || {})
            .filter((k) => String(k).toLowerCase().includes("rent")),
          sourcingInputsRentKeys: Object.keys(applicationPayload?.meta?.sourcingInputs || {})
            .filter((k) => String(k).toLowerCase().includes("rent")),
        });
      }

      // Build payload for illustration exactly like borrower portal: backend fields take priority (repayment_type, cost_over_product_term, loan_amount)
      const costOverProductTerm =
        fullApp.cost_over_product_term ??
        applicationPayload?.cost_over_product_term ??
        productData?.cost_over_product_term ??
        productData?.total_cost ??
        productData?.totalCost ??
        productData?._raw?.cost_over_product_term ??
        productData?._raw?.total_cost;

      const fromPayload = applicationPayload?.loan_amount ?? applicationPayload?.mortgageAmount;
      const applicationLoanAmountForPayload =
        fullApp.loan_amount ?? fullApp.mortgage_amount ?? (loanAmountRaw || fromPayload);

      const payloadForIllustration = {
        ...(applicationPayload || {}),
        repayment_type: fullApp.repayment_type ?? fullApp.repaymentType ?? applicationPayload?.repayment_type ?? applicationPayload?.repaymentType ?? "Repayment",
        repaymentType: fullApp.repayment_type ?? fullApp.repaymentType ?? applicationPayload?.repaymentType ?? applicationPayload?.repayment_type ?? "Repayment",
        loan_amount: applicationLoanAmountForPayload ?? applicationPayload?.loan_amount,
        mortgageAmount: applicationLoanAmountForPayload ?? applicationPayload?.mortgageAmount,
        cost_over_product_term: costOverProductTerm,
        deposit_amount: applicationDepositAmount ?? 0,
        est_rent:
          (applicationEstRent != null && applicationEstRent > 0)
            ? applicationEstRent
            : (Number(applicationPayload?.est_rent) > 0
              ? Number(applicationPayload.est_rent)
              : (Number(applicationPayload?.estimated_rent) > 0
                ? Number(applicationPayload.estimated_rent)
                : (Number(applicationPayload?.estimatedRent) > 0
                  ? Number(applicationPayload.estimatedRent)
                  : 0))),
        estimated_rent:
          (applicationEstRent != null && applicationEstRent > 0)
            ? applicationEstRent
            : (Number(applicationPayload?.estimated_rent) > 0
              ? Number(applicationPayload.estimated_rent)
              : (Number(applicationPayload?.est_rent) > 0
                ? Number(applicationPayload.est_rent)
                : (Number(applicationPayload?.estimatedRent) > 0
                  ? Number(applicationPayload.estimatedRent)
                  : 0))),
        productType: fullApp.product_type || applicationPayload?.productType || productData?.productType || productData?.category,
        product_type: fullApp.product_type || applicationPayload?.product_type,
        loan_purpose: fullApp.loan_purpose || applicationPayload?.loan_purpose,
        sector: fullApp.sector ?? applicationPayload?.sector ?? productData?.sector,
        facility_required: fullApp.facility_required ?? applicationPayload?.facility_required ?? productData?.facility_required,
        facility_type: fullApp.facility_type ?? applicationPayload?.facility_type ?? productData?.facility_type,
        net_advance: fullApp.net_advance ?? applicationPayload?.net_advance ?? productData?.net_advance,
        contract_term_months: fullApp.contract_term_months ?? applicationPayload?.contract_term_months ?? productData?.contract_term_months ?? productData?.termMonths,
        min_service_fee: fullApp.min_service_fee ?? applicationPayload?.min_service_fee ?? productData?.min_service_fee,
        ip_percent: fullApp.ip_percent ?? applicationPayload?.ip_percent ?? productData?.ip_percent,
        simple_service_fee:
          fullApp.simple_service_fee ??
          applicationPayload?.simple_service_fee ??
          productData?.simple_service_fee,
        service_fee_percent:
          fullApp.service_fee_percent ??
          applicationPayload?.service_fee_percent ??
          productData?.service_fee_percent ??
          productData?.annualCostPct ??
          fullApp.simple_service_fee ??
          applicationPayload?.simple_service_fee ??
          productData?.simple_service_fee,
        discount_rate_percent: fullApp.discount_rate_percent ?? applicationPayload?.discount_rate_percent ?? productData?.discount_rate_percent,
        apr: fullApp.apr ?? applicationPayload?.apr ?? productData?.apr,
        arrangement_fee: fullApp.arrangement_fee ?? applicationPayload?.arrangement_fee ?? productData?.arrangement_fee,
        additional_borrowing_amount: fullApp.additional_borrowing_amount ?? applicationPayload?.additional_borrowing_amount ?? productData?.additional_borrowing_amount,
        interest_rate: fullApp.interest_rate ?? applicationPayload?.interest_rate ?? productData?.interest_rate,
        additional_borrowing_interest_rate: fullApp.interest_rate ?? fullApp.additional_borrowing_interest_rate ?? applicationPayload?.additional_borrowing_interest_rate ?? productData?.additional_borrowing_interest_rate,
        first_12_month_interest: fullApp.first_12_month_interest ?? applicationPayload?.first_12_month_interest ?? productData?.first_12_month_interest,
        monthly_repayments: fullApp.monthly_repayments ?? applicationPayload?.monthly_repayments ?? productData?.monthly_repayments,
        total_repayable: fullApp.total_repayable ?? applicationPayload?.total_repayable ?? productData?.total_repayable,
        confidential_facility: fullApp.confidential_facility ?? applicationPayload?.confidential_facility ?? productData?.confidential_facility,
        disclosure: fullApp.disclosure ?? applicationPayload?.disclosure ?? productData?.disclosure,
        ab_arrangement_fee:
          fullApp.ab_arrangement_fee ??
          applicationPayload?.ab_arrangement_fee ??
          productData?.ab_arrangement_fee,
        ab_cost_over_product_term:
          fullApp.ab_cost_over_product_term ??
          applicationPayload?.ab_cost_over_product_term ??
          fullApp.ab_cost_over_term ??
          applicationPayload?.ab_cost_over_term ??
          productData?.ab_cost_over_product_term ??
          productData?.ab_cost_over_term,
        abCalc: productData?.abCalc || applicationPayload?.abCalc || null,
      };

      const illustrationData = extractIllustrationData(
        {
          ...productData,
          // Always prefer the real Mortgage Product / Application product ID (not Confidential/Disclosed title)
          name: productData?.name || productId || productData?.product_id,
          product_id: productData?.product_id || productData?.name || productId,
          productId: productData?.productId || productData?.name || productId,
          mortgage_product: productData?.mortgage_product || productId,
          product_category: fullApp.product_type || productData?.product_category || productData?.category,
          category: fullApp.product_type || productData?.category,
          productType: fullApp.product_type || productData?.productType,
          simple_service_fee:
            productData?.simple_service_fee ??
            fullApp.simple_service_fee ??
            applicationPayload?.simple_service_fee,
          annualCostPct:
            productData?.annualCostPct ??
            productData?.service_fee_percent ??
            fullApp.service_fee_percent ??
            fullApp.simple_service_fee ??
            applicationPayload?.simple_service_fee,
          min_service_fee:
            productData?.min_service_fee ??
            fullApp.min_service_fee ??
            applicationPayload?.min_service_fee,
        },
        payloadForIllustration,
        applicationLoanAmount,
        applicationPropertyValue
      );

      const isIfIllustration = !!illustrationData?.isInvoiceFinance;
      const propertyAddress = isIfIllustration
        ? ""
        : (
        fullApp.property_address ??
        fullApp.propertyAddress ??
        applicationPayload?.property_address ??
        applicationPayload?.propertyAddress ??
        applicationPayload?.formData?.propertyAddress ??
        applicationPayload?.formData?.property_address ??
        applicationPayload?.btl?.solo?.propertyAddress ??
        applicationPayload?.btl?.limited?.propertyAddress ??
        applicationPayload?.btl?.partnership?.propertyAddress ??
        applicationPayload?.btl?.pension?.propertyAddress ??
        application?.propertyAddress ??
        ""
      );

      const ownershipType = fullApp.ownership_type ?? applicationPayload?.ownership_type ?? "";
      const companyName = fullApp.company_name ??
        applicationPayload?.company_name ??
        applicationPayload?.btl?.limited?.company?.name ??
        "";
      const clientDetails = {
        name: (fullApp.first_name && fullApp.surname)
          ? `${fullApp.first_name} ${fullApp.surname}`
          : (applicationPayload?.formData?.firstName && applicationPayload?.formData?.surname)
            ? `${applicationPayload.formData.firstName} ${applicationPayload.formData.surname}`
            : application.borrower.name || "Client",
        propertyAddress: typeof propertyAddress === "string" ? propertyAddress : (propertyAddress?.address || ""),
        telephone: fullApp.phone_number ??
          applicationPayload?.formData?.phone ??
          applicationPayload?.formData?.applicants?.[0]?.phone ??
          application.borrower.phone ??
          "",
        email: fullApp.email ??
          fullApp.applicant ??
          applicationPayload?.formData?.email ??
          applicationPayload?.formData?.applicants?.[0]?.email ??
          application.borrower.email ??
          "",
        ownershipType: ownershipType || undefined,
        companyName: companyName || undefined,
      };

      if (mode === "eor") {
        const eorSearch = isIfIllustration
          ? await fetchIfEorSearchResults({
              applicationPayload,
              payloadForIllustration,
              fullApp,
            })
          : await fetchEorSearchResults({
              applicationPayload,
              payloadForIllustration,
              applicationLoanAmount,
              applicationPropertyValue,
              applicationDepositAmount,
              applicationEstRent,
            });
        let searchResults = Array.isArray(eorSearch?.rows) ? eorSearch.rows : [];
        const appliedFilters = eorSearch?.appliedFilters || {};
        const targetProductId = String(productId || illustrationData?.productId || "").trim().toLowerCase();
        const hasSelectedInResults = searchResults.some((row) => {
          const id = String(row?.name || row?.product_id || row?.id || "").trim().toLowerCase();
          return Boolean(targetProductId) && id === targetProductId;
        });
        if (!hasSelectedInResults && productData) {
          // Ensure selected product is visible even if API result window excludes it.
          // Enrich with pricing/type fields used by EOR table when missing from /api/resource payload.
          const selectedCalc = matchingShortlist?.calc_selected || {};
          const selectedRow = isIfIllustration
            ? {
                ...productData,
                name: productData?.name || productId,
                product_id: productData?.name || productId,
                id: productData?.name || productId,
                product_name:
                  productData?.product_name ||
                  illustrationData?.productName ||
                  productId,
                lender_name:
                  productData?.lender_name ||
                  productData?.lender_product_code ||
                  "",
                apr:
                  productData?.apr ??
                  productData?.discount_rate_percent ??
                  null,
                ratePct:
                  productData?.apr ??
                  productData?.discount_rate_percent ??
                  null,
                ip_percent:
                  productData?.ip_percent ??
                  applicationPayload?.ip_percent ??
                  null,
                invoicePurchasePct:
                  productData?.ip_percent ??
                  applicationPayload?.ip_percent ??
                  null,
                service_fee_percent:
                  productData?.service_fee_percent ??
                  productData?.simple_service_fee ??
                  applicationPayload?.service_fee_percent ??
                  null,
                annualCostPct:
                  productData?.service_fee_percent ??
                  productData?.simple_service_fee ??
                  null,
                termMonths:
                  productData?.contract_term_months ??
                  productData?.termMonths ??
                  applicationPayload?.contract_term_months ??
                  null,
                contract_term_months:
                  productData?.contract_term_months ??
                  applicationPayload?.contract_term_months ??
                  null,
                net_advance:
                  productData?.net_advance ??
                  applicationPayload?.net_advance ??
                  null,
                netAdvance:
                  productData?.net_advance ??
                  applicationPayload?.net_advance ??
                  null,
                cost_over_product_term:
                  Number.isFinite(Number(costOverProductTerm))
                    ? Number(costOverProductTerm)
                    : productData?.cost_over_product_term ?? null,
                costOverProductTerm:
                  Number.isFinite(Number(costOverProductTerm))
                    ? Number(costOverProductTerm)
                    : productData?.cost_over_product_term ?? null,
              }
            : {
            ...productData,
            name: productData?.name || productId,
            product_name: productData?.product_name || illustrationData?.productName || productId,
            product_termsyears:
              productData?.product_termsyears ??
              productData?.product_term_years ??
              illustrationData?.productTermYears ??
              null,
            product_type:
              productData?.product_type ??
              illustrationData?.productTypeDisplay ??
              null,
            product_type_list:
              Array.isArray(productData?.product_type_list) && productData.product_type_list.length
                ? productData.product_type_list
                : (illustrationData?.productTypeDisplay ? [illustrationData.productTypeDisplay] : []),
            calc_selected: {
              monthly_payment:
                selectedCalc?.monthly_payment ??
                matchingShortlist?.monthly_payment ??
                safeNum(illustrationData?.fixedMonthlyPaymentDisplay, null),
              cost_over_product_term:
                selectedCalc?.cost_over_product_term ??
                matchingShortlist?.cost_over_product_term ??
                (Number.isFinite(Number(costOverProductTerm)) ? Number(costOverProductTerm) : null),
              total_fees:
                selectedCalc?.total_fees ??
                matchingShortlist?.total_fees ??
                matchingShortlist?.arrangement_fee ??
                productData?.total_fees ??
                null,
            },
          };
          searchResults = [...searchResults, selectedRow];
        }
        // Keep selected product in natural rate ordering instead of pinning to top.
        const withRate = searchResults
          .map((row, idx) => ({
            row,
            idx,
            rate: Number(
              isIfIllustration
                ? (row?.apr ?? row?.ratePct ?? row?.discount_rate_percent ?? Number.POSITIVE_INFINITY)
                : (row?.product_rate_percent ?? row?.calc_selected?.interest_rate ?? Number.POSITIVE_INFINITY)
            ),
          }))
          .sort((a, b) => (a.rate - b.rate) || (a.idx - b.idx))
          .map((x) => x.row);
        // De-duplicate by product id/name while preserving sorted order, then cap to 100.
        const seen = new Set();
        const dedupedSorted = withRate.filter((row) => {
          const id = String(row?.name || row?.product_id || row?.id || "").trim().toLowerCase();
          if (!id) return true;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });

        // Build a 50-row window centered on selected product:
        // 20 above + selected + 29 below (with edge balancing where needed).
        const targetWindowSize = 50;
        const selectedIdx = dedupedSorted.findIndex((row) => {
          const id = String(row?.name || row?.product_id || row?.id || "").trim().toLowerCase();
          return Boolean(targetProductId) && id === targetProductId;
        });

        if (selectedIdx >= 0) {
          const preferredAbove = 20;
          const preferredBelow = 29;
          let start = Math.max(0, selectedIdx - preferredAbove);
          let end = Math.min(dedupedSorted.length, selectedIdx + preferredBelow + 1); // end exclusive

          let windowSize = end - start;
          if (windowSize < targetWindowSize) {
            const remaining = targetWindowSize - windowSize;
            const canGrowDown = dedupedSorted.length - end;
            const growDown = Math.min(remaining, canGrowDown);
            end += growDown;
            windowSize = end - start;
          }
          if (windowSize < targetWindowSize) {
            const remaining = targetWindowSize - windowSize;
            const canGrowUp = start;
            const growUp = Math.min(remaining, canGrowUp);
            start -= growUp;
          }

          searchResults = dedupedSorted.slice(start, end);
        } else {
          searchResults = dedupedSorted.slice(0, targetWindowSize);
        }
        const lenderUsers = await fetchLenderUsers();
        const lenderNames = toSortedLenderNames(lenderUsers);
        if (isIfIllustration) {
          searchResults = await applyIfLenderNames(searchResults, lenderUsers);
        }
        await generateEvidenceOfResearchPDF(illustrationData, clientDetails, {
          searchResults,
          lenderNames,
          selectedProductId: productId || illustrationData?.productId,
          appliedFilters,
        });
      } else {
        await generateClientIllustrationPDF(illustrationData, clientDetails);
      }
    } catch (error) {
      console.error("Error generating document:", error);
      alert("Failed to generate document. Please try again.");
    } finally {
      isGeneratingESISRef.current = false;
    }
  };

  const handleMarkContacted = (lenderId) => {
    setLenders((prevLenders) =>
      prevLenders.map((lender) =>
        lender.id === lenderId
          ? {
            ...lender,
            lastLoginDate: new Date().toISOString().split("T")[0],
            daysSinceLogin: 0,
            status: "Active",
          }
          : lender
      )
    );
  };

  const handleGoLiveProduct = (productId) => {
    if (window.confirm("Activate this product now?")) {
      setScheduledProducts((prevProducts) =>
        prevProducts.map((product) =>
          product.id === productId ? { ...product, status: "Live" } : product
        )
      );
    }
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setProductLineFilter("");
    setLoanPurposeFilter("");
    setFacilityTypeFilter("");
    setAssigneeFilter("");
    setSortBy("modified");
    setSortOrder("desc");
  };

  const hasActiveFilters =
    searchTerm ||
    statusFilter ||
    productLineFilter ||
    loanPurposeFilter ||
    facilityTypeFilter ||
    assigneeFilter ||
    sortBy !== "modified";

  const allStatuses = APPLICATION_STAGES.map((s) => s.value);
  const uniqueLoanPurposes = [
    ...new Set(
      applications
        .filter((a) => (a.productLine || "BTL") === "BTL")
        .map((a) => a.loanPurpose || a.productType)
        .filter(Boolean)
    ),
  ];
  const uniqueAssignees = [...new Set(applications.map((a) => a.assignedTo))].filter((a) => a !== "Unassigned");

  // When Product = All: one generic "Application Type" list (BTL loan purposes + IF facilities)
  const allApplicationTypeOptions = [
    ...uniqueLoanPurposes,
    ...IF_FACILITY_OPTIONS.filter((opt) => !uniqueLoanPurposes.includes(opt)),
  ];
  const applicationTypeFilterValue = loanPurposeFilter || facilityTypeFilter;

  const handleProductLineFilterChange = (value) => {
    setProductLineFilter(value);
    if (value === "IF") {
      setLoanPurposeFilter("");
      if (sortBy === "loanPurpose" || sortBy === "productLine" || sortBy === "applicationType") {
        setSortBy("facility");
      }
    } else if (value === "BTL") {
      setFacilityTypeFilter("");
      if (sortBy === "facility" || sortBy === "productLine" || sortBy === "applicationType") {
        setSortBy("loanPurpose");
      }
    } else if (!value) {
      if (sortBy === "loanPurpose" || sortBy === "facility") {
        setSortBy("applicationType");
      }
    }
  };

  const sortByOptions = !productLineFilter
    ? ["modified", "amount", "borrower", "productLine", "applicationType"]
    : productLineFilter === "BTL"
      ? ["modified", "amount", "borrower", "loanPurpose"]
      : ["modified", "amount", "borrower", "facility"];

  const handleApplicationTypeFilterChange = (value) => {
    const isFacility = IF_FACILITY_OPTIONS.some(
      (opt) => opt.toLowerCase() === String(value || "").toLowerCase()
    );
    if (!value) {
      setLoanPurposeFilter("");
      setFacilityTypeFilter("");
      return;
    }
    if (isFacility) {
      setFacilityTypeFilter(value);
      setLoanPurposeFilter("");
    } else {
      setLoanPurposeFilter(value);
      setFacilityTypeFilter("");
    }
  };

  return (
    <div className="space-y-3">
      {/* Date Navigation Header */}
      <div className="bg-white rounded-2xl border border-slate-200 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-xl bg-sky-50 flex items-center justify-center">
              <CalendarIcon className="h-4 w-4 text-sky-600" />
            </div>
            <div>
              <h1 className="text-xs md:text-sm font-semibold text-slate-900">Daily Application Diary</h1>
              <p className="text-[10px] text-slate-500">Time to Cash Tracking & Management</p>
            </div>
          </div>
        </div>

        {/* Date Navigator */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <button
              onClick={goToPreviousDay}
              className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="h-4 w-4 text-slate-600" />
            </button>
            <div className="text-xs font-semibold text-slate-900 min-w-[240px] text-center">
              {formatDateHeader(currentDate)}
            </div>
            <button
              onClick={goToNextDay}
              className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ChevronRight className="h-4 w-4 text-slate-600" />
            </button>
          </div>

          <button
            onClick={goToToday}
            disabled={isToday()}
            className="px-2.5 py-1 rounded-lg border border-slate-200 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Today
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-wrap items-center gap-1.5">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by ID, borrower, product, or property..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-400"
            />
          </div>

          <FilterDropdown label="Status" options={allStatuses} value={statusFilter} onChange={setStatusFilter} icon={Filter} />
          <FilterDropdown
            label="Product"
            options={PRODUCT_LINE_OPTIONS}
            value={productLineFilter}
            onChange={handleProductLineFilterChange}
            icon={Package}
          />
          {!productLineFilter ? (
            <FilterDropdown
              label="Application Type"
              options={allApplicationTypeOptions}
              value={applicationTypeFilterValue}
              onChange={handleApplicationTypeFilterChange}
              icon={Tag}
            />
          ) : productLineFilter === "BTL" ? (
            <FilterDropdown
              label="Loan Purpose"
              options={uniqueLoanPurposes}
              value={loanPurposeFilter}
              onChange={setLoanPurposeFilter}
              icon={Building}
            />
          ) : (
            <FilterDropdown
              label="Facility"
              options={IF_FACILITY_OPTIONS}
              value={facilityTypeFilter}
              onChange={setFacilityTypeFilter}
              icon={Tag}
            />
          )}
          <FilterDropdown label="Assignee" options={uniqueAssignees} value={assigneeFilter} onChange={setAssigneeFilter} icon={User} />
          <FilterDropdown
            label="Sort By"
            options={sortByOptions}
            value={sortBy}
            onChange={(v) => setSortBy(v || "modified")}
            optionLabels={SORT_OPTION_LABELS}
            icon={ArrowUpDown}
          />

          <button
            type="button"
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition"
          >
            {sortOrder === "asc" ? "↑" : "↓"}
          </button>

          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition"
            >
              <X className="h-3 w-3" />
              Clear All
            </button>
          )}
        </div>

        <div className="text-[11px] text-slate-500 mt-2">
          Showing {filteredApplications.length} ongoing application{filteredApplications.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* ROW 1: Ongoing Applications - Time to Cash Tracking */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-sky-600" />
            <h2 className="text-xs font-semibold text-slate-900">Ongoing Applications - Time to Cash Tracking</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchData}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition"
            >
              <RefreshCw className="h-3 w-3" />
              Refresh
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={filteredApplications.length > 0 && selectedIds.size === filteredApplications.length}
                    onChange={handleSelectAll}
                    className="rounded border-slate-300 text-sky-500 focus:ring-sky-400"
                  />
                </th>
                <th className="px-3 py-2 text-left font-medium">App ID</th>
                <th className="px-3 py-2 text-left font-medium">Borrower</th>
                <th className="px-3 py-2 text-left font-medium">Amount</th>
                <th className="px-3 py-2 text-left font-medium">Current Stage</th>
                <th className="px-3 py-2 text-left font-medium">Time to Cash Progress</th>
                <th className="px-3 py-2 text-left font-medium">Assigned To</th>
                <th className="px-3 py-2 text-left font-medium">Updated</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableSkeleton rows={8} cols={9} />
              ) : paginatedApplications.length > 0 ? (
                paginatedApplications.map((app) => (
                  <tr
                    key={app.id}
                    className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 transition cursor-pointer ${selectedIds.has(app.id) ? "bg-sky-50" : ""
                      }`}
                    onClick={(e) => {
                      if (e.target.type !== "checkbox" && !e.target.closest("button") && !e.target.closest("a")) {
                        setSelectedApp(app);
                      }
                    }}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(app.id)}
                        onChange={() => handleSelectOne(app.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded border-slate-300 text-sky-500 focus:ring-sky-400"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div>
                        <div className="font-medium text-slate-900">{app.id}</div>
                        <div className="text-[11px] text-slate-500">
                          Product:{" "}
                          <span className="text-sky-600 font-medium">
                            {app.product?.productId || "—"}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div>
                        <div className="font-medium text-slate-900">{app.borrower.name}</div>
                        <div className="text-[11px] text-slate-500">{app.borrower.email}</div>
                      </div>
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-900">{money(app.loanAmount)}</td>
                    <td className="px-3 py-2">
                      <StatusBadge
                        status={
                          isInvoiceFinanceApplication(app) &&
                          (app.status === "Valuation" || app.currentStage === "Valuation")
                            ? "Audit"
                            : app.status
                        }
                        tone={app.statusTone}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="min-w-[160px]">
                        <TimeToCashProgress application={app} />
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {app.assignedTo && app.assignedTo !== "Unassigned" ? (
                          <>
                            <div className="h-5 w-5 bg-sky-100 rounded-full flex items-center justify-center">
                              <span className="text-[10px] font-medium text-sky-600">
                                {app.assignedTo
                                  .split(" ")
                                  .map((n) => n[0])
                                  .join("")}
                              </span>
                            </div>
                            <span className="text-[11px] text-slate-700">{app.assignedTo.split(" ")[0]}</span>
                          </>
                        ) : (
                          <span className="text-[11px] text-amber-600">Unassigned</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-500">{formatRelativeTime(app.modified || app.lastUpdated)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadESIS(app, "eor");
                          }}
                          className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition"
                          title={DEMO_DOCS.eor.full}
                        >
                          <Download className="h-4 w-4" />
                          {DEMO_DOCS.eor.label}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadESIS(app);
                          }}
                          className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition"
                          title={DEMO_DOCS.esis.full}
                        >
                          <Download className="h-4 w-4" />
                          {DEMO_DOCS.esis.label}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedApp(app);
                          }}
                          className="p-1 hover:bg-slate-100 rounded transition"
                          title="View Details"
                        >
                          <Eye className="h-3.5 w-3.5 text-slate-600" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setStatusUpdateApp(app);
                          }}
                          className="p-1 hover:bg-sky-50 rounded transition"
                          title="Update Status"
                        >
                          <Edit className="h-3.5 w-3.5 text-sky-600" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setAssignModalApp(app);
                          }}
                          className="p-1 hover:bg-purple-50 rounded transition"
                          title="Assign"
                        >
                          <UserCheck className="h-3.5 w-3.5 text-purple-600" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteApp(app);
                          }}
                          className="p-1 hover:bg-red-50 rounded transition"
                          title="Delete application"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="px-3 py-8 text-center text-xs text-slate-500">
                    <div className="flex flex-col items-center gap-2">
                      <Search className="h-8 w-8 text-slate-300" />
                      <p>No applications found</p>
                      {!searchTerm && !statusFilter && !productLineFilter && !loanPurposeFilter && !facilityTypeFilter && !assigneeFilter && (
                        <p className="text-xs max-w-sm">
                          If you expect to see applications, your user may need permission to list Applications in Frappe (e.g. System Manager or Read on Applications doctype).
                        </p>
                      )}
                      <button onClick={clearAllFilters} className="text-sky-600 hover:text-sky-700 text-xs font-medium">
                        Clear filters
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination for applications (mirrors ProductManagementPage) */}
      <div className="mt-4">
        <div className="bg-white rounded-2xl border border-slate-200 px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-sm text-slate-600">
              Page {currentPage} of{" "}
              {Math.max(
                1,
                Math.ceil(
                  filteredApplications.length /
                    (itemsPerPage >= (ALL_PAGE_SIZE || 1000000) ? filteredApplications.length || 1 : itemsPerPage)
                )
              )}
              {filteredApplications.length != null && (
                <span className="ml-2">({filteredApplications.length} applications)</span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {Math.ceil(
                filteredApplications.length /
                  (itemsPerPage >= (ALL_PAGE_SIZE || 1000000) ? filteredApplications.length || 1 : itemsPerPage)
              ) > 1 && (
                <>
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    First
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Previous"
                  >
                    <ArrowUpDown className="h-4 w-4 rotate-90" />
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from(
                      {
                        length: Math.max(
                          1,
                          Math.ceil(
                            filteredApplications.length /
                              (itemsPerPage >= (ALL_PAGE_SIZE || 1000000)
                                ? filteredApplications.length || 1
                                : itemsPerPage)
                          )
                        ),
                      },
                      (_, i) => i + 1
                    )
                      .filter(
                        (p) =>
                          p === 1 ||
                          p ===
                            Math.max(
                              1,
                              Math.ceil(
                                filteredApplications.length /
                                  (itemsPerPage >= (ALL_PAGE_SIZE || 1000000)
                                    ? filteredApplications.length || 1
                                    : itemsPerPage)
                              )
                            ) ||
                          (p >= currentPage - 2 && p <= currentPage + 2)
                      )
                      .map((p, idx, arr) => {
                        const prev = arr[idx - 1];
                        const totalPages = Math.max(
                          1,
                          Math.ceil(
                            filteredApplications.length /
                              (itemsPerPage >= (ALL_PAGE_SIZE || 1000000)
                                ? filteredApplications.length || 1
                                : itemsPerPage)
                          )
                        );
                        const showDots = prev && p - prev > 1;
                        return (
                          <React.Fragment key={p}>
                            {showDots ? <span className="px-2 text-slate-400">.</span> : null}
                            <button
                              onClick={() => setCurrentPage(p)}
                              className={`min-w-[2.5rem] px-3 py-1.5 text-sm font-medium rounded-lg transition ${
                                currentPage === p
                                  ? "bg-sky-500 text-white"
                                  : "text-slate-700 bg-white border border-slate-200 hover:bg-slate-50"
                              }`}
                            >
                              {p}
                            </button>
                          </React.Fragment>
                        );
                      })}
                  </div>
                  <button
                    onClick={() =>
                      setCurrentPage((p) =>
                        Math.min(
                          Math.max(
                            1,
                            Math.ceil(
                              filteredApplications.length /
                                (itemsPerPage >= (ALL_PAGE_SIZE || 1000000)
                                  ? filteredApplications.length || 1
                                  : itemsPerPage)
                            )
                          ),
                          p + 1
                        )
                      )
                    }
                    disabled={
                      currentPage ===
                      Math.max(
                        1,
                        Math.ceil(
                          filteredApplications.length /
                            (itemsPerPage >= (ALL_PAGE_SIZE || 1000000)
                              ? filteredApplications.length || 1
                              : itemsPerPage)
                        )
                      )
                    }
                    className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Next"
                  >
                    <ArrowUpDown className="h-4 w-4 -rotate-90" />
                  </button>
                  <button
                    onClick={() =>
                      setCurrentPage(
                        Math.max(
                          1,
                          Math.ceil(
                            filteredApplications.length /
                              (itemsPerPage >= (ALL_PAGE_SIZE || 1000000)
                                ? filteredApplications.length || 1
                                : itemsPerPage)
                          )
                        )
                      )
                    }
                    disabled={
                      currentPage ===
                      Math.max(
                        1,
                        Math.ceil(
                          filteredApplications.length /
                            (itemsPerPage >= (ALL_PAGE_SIZE || 1000000)
                              ? filteredApplications.length || 1
                              : itemsPerPage)
                        )
                      )
                    }
                    className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Last
                  </button>
                </>
              )}

              <div className="flex items-center gap-2">
                <label htmlFor="apps-per-page" className="text-sm text-slate-600 whitespace-nowrap">
                  Per page:
                </label>
                <select
                  id="apps-per-page"
                  value={itemsPerPage >= (ALL_PAGE_SIZE || 1000000) ? "all" : itemsPerPage}
                  onChange={(e) => {
                    const val = e.target.value;
                    const nextPageSize = val === "all" ? ALL_PAGE_SIZE : Number(val) || 25;
                    setItemsPerPage(nextPageSize);
                    setCurrentPage(1);
                  }}
                  className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700 focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                >
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                  <option value="all">All</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ROW 2 & 3: Lender Portal Alerts and Scheduled Products side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Lender Portal Alerts - Inactive 30+ Days */}
      <div className="bg-white rounded-2xl border border-red-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-red-200 bg-red-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <h2 className="text-sm font-semibold text-red-900">Lender Portal Alerts - Inactive 30+ Days</h2>
          </div>
          <div className="flex items-center gap-2 text-xs text-red-600">
            <Bell className="h-4 w-4" />
            <span>{inactiveLenders.length} Inactive</span>
          </div>
        </div>

        <div className="p-4 max-h-[420px] overflow-y-auto">
          {inactiveLenders.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {inactiveLenders.map((lender) => (
                <div
                  key={lender.id}
                  className="border border-red-200 rounded-xl p-4 bg-red-50/50 hover:bg-red-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Building className="h-5 w-5 text-red-600" />
                        <h3 className="text-sm font-semibold text-red-900">{lender.name}</h3>
                      </div>
                      <div className="text-xs text-red-700 font-medium">
                        Last login: {lender.daysSinceLogin} days ago ({formatDate(lender.lastLoginDate)})
                      </div>
                    </div>
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium border border-red-200">
                      Inactive
                    </span>
                  </div>

                  <div className="space-y-2 mb-3">
                    <div className="flex items-center gap-2 text-xs">
                      <User className="h-4 w-4 text-red-500" />
                      <span className="text-slate-700">Contact: {lender.contactPerson}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <Phone className="h-4 w-4 text-red-500" />
                      <a href={`tel:${lender.phone}`} className="text-blue-600 hover:text-blue-700">
                        {lender.phone}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <Mail className="h-4 w-4 text-red-500" />
                      <a href={`mailto:${lender.email}`} className="text-blue-600 hover:text-blue-700">
                        {lender.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <FileText className="h-4 w-4 text-red-500" />
                      <span className="text-slate-700">{lender.activeApplications} active applications</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <a
                      href={`tel:${lender.phone}`}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-blue-500 text-white text-xs font-medium hover:bg-blue-600 transition-colors"
                    >
                      <Phone className="h-4 w-4" />
                      Call
                    </a>
                    <a
                      href={`mailto:${lender.email}`}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-purple-500 text-white text-xs font-medium hover:bg-purple-600 transition-colors"
                    >
                      <Mail className="h-4 w-4" />
                      Email
                    </a>
                    <button
                      onClick={() => handleMarkContacted(lender.id)}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-green-500 text-white text-xs font-medium hover:bg-green-600 transition-colors"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Contacted
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <p className="text-sm text-slate-600">All lenders are active! 🎉</p>
              <p className="text-xs text-slate-500">No lenders have been inactive for 30+ days</p>
            </div>
          )}
        </div>
      </div>

      {/* Scheduled Products */}
      <div className="bg-white rounded-2xl border border-green-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-green-200 bg-green-50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-green-600" />
              <h2 className="text-sm font-semibold text-green-900">Scheduled Products</h2>
            </div>
            <div className="flex items-center gap-2 text-xs text-green-600">
              <Clock className="h-4 w-4" />
              <span>{todaysProducts.length} Scheduled</span>
            </div>
          </div>

          {/* Date selector */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-slate-600">
              Show products for:
            </label>
            <input
              type="date"
              value={selectedScheduledDate.toISOString().split('T')[0]}
              onChange={(e) => {
                const newDate = new Date(e.target.value);
                newDate.setHours(0, 0, 0, 0);
                setSelectedScheduledDate(newDate);
              }}
              className="px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
            />
            {selectedScheduledDate.toDateString() === new Date().toDateString() && (
              <span className="text-xs text-green-600 font-medium">(Today)</span>
            )}
          </div>
        </div>

        <div className="p-4 max-h-[420px] overflow-y-auto">
          {todaysProducts.length > 0 ? (
            <div className="space-y-3">
              {todaysProducts.map((product) => (
                <div
                  key={product.id}
                  className="border border-green-200 rounded-xl p-4 bg-green-50/50 hover:bg-green-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Tag className="h-5 w-5 text-green-600" />
                        <h3 className="text-sm font-semibold text-green-900">{product.name}</h3>
                      </div>
                      <div className="text-xs text-green-700">
                        {product.productId} • {product.lender}
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${product.status === "Live"
                        ? "bg-green-100 text-green-700 border-green-200"
                        : "bg-amber-100 text-amber-700 border-amber-200"
                      }`}>
                      {product.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                    <div className="text-xs">
                      <div className="text-slate-600">Loan Purpose</div>
                      <div className="font-medium text-slate-900">{product.productType}</div>
                    </div>
                    <div className="text-xs">
                      <div className="text-slate-600">Rate</div>
                      <div className="font-semibold text-slate-900">{product.rate}%</div>
                    </div>
                    <div className="text-xs">
                      <div className="text-slate-600">Go Live Time</div>
                      <div className="font-medium text-slate-900">{product.goLiveTime}</div>
                    </div>
                  </div>

                  <div className="bg-white border border-green-200 rounded-lg p-3 mb-3">
                    <div className="text-xs text-slate-600 mb-1">Changes:</div>
                    <div className="text-xs text-slate-900">{product.changes}</div>
                  </div>

                  <div className="flex items-center gap-2">
                    {product.status === "Scheduled" ? (
                      <button
                        onClick={() => handleGoLiveProduct(product.id)}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-green-500 text-white text-xs font-medium hover:bg-green-600 transition-colors"
                      >
                        <PlayCircle className="h-4 w-4" />
                        Go Live Now
                      </button>
                    ) : (
                      <div className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-green-100 text-green-700 text-xs font-medium">
                        <CheckCircle className="h-4 w-4" />
                        Product is Live
                      </div>
                    )}
                    <button className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 text-xs font-medium hover:bg-slate-50 transition-colors">
                      <Eye className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Clock className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-600">No products scheduled for today</p>
              <p className="text-xs text-slate-500">Check back tomorrow for scheduled launches</p>
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Modals */}
      {selectedApp && (
        <ApplicationDetailModal
          application={selectedApp}
          onClose={() => setSelectedApp(null)}
          onOpenStatusUpdate={(app) => {
            setSelectedApp(null);
            setStatusUpdateApp(app);
          }}
          onOpenAssignModal={(app) => {
            setSelectedApp(null);
            setAssignModalApp(app);
          }}
        />
      )}

      {statusUpdateApp && (
        <StatusUpdateModal
          application={statusUpdateApp}
          onClose={() => setStatusUpdateApp(null)}
          onUpdateStatus={handleUpdateStatus}
        />
      )}

      {assignModalApp && (
        <AssignModal
          application={assignModalApp}
          onClose={() => setAssignModalApp(null)}
          onAssign={handleAssign}
        />
      )}

      {deleteApp && (
        <DeleteApplicationModal
          application={deleteApp}
          onClose={() => setDeleteApp(null)}
          onConfirm={handleDeleteApplication}
        />
      )}
    </div>
  );
}

function DeleteApplicationModal({ application, onClose, onConfirm }) {
  const [confirmText, setConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    if (confirmText !== application.id || isDeleting) return;
    setIsDeleting(true);
    try {
      await onConfirm(application);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
              <Trash2 className="h-4 w-4 text-red-600" />
            </div>
            <h2 className="text-sm font-semibold text-slate-900">Delete application</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-slate-100"
            disabled={isDeleting}
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-3 text-sm text-slate-700">
          <p>
            This will permanently delete application{" "}
            <span className="font-semibold text-slate-900">{application.id}</span> from the
            platform.
          </p>
          <p className="text-xs text-slate-500">
            To confirm, please type the application ID exactly as shown below.
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono text-slate-800">
            {application.id}
          </div>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            placeholder="Type application ID to confirm"
          />
        </div>
        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button
            type="button"
            className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-100"
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-3 py-1.5 text-xs font-medium text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={handleConfirm}
            disabled={confirmText !== application.id || isDeleting}
          >
            {isDeleting ? "Deleting…" : "Delete application"}
          </button>
        </div>
      </div>
    </div>
  );
}